import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, IconButton, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Autocomplete, MenuItem, Select, FormControl, InputLabel,
  Grid, Paper, Alert, CircularProgress, Divider, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Checkbox, FormControlLabel
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, Block as VoidIcon, DeleteForever as TrashIcon } from '@mui/icons-material';
import {
  createWholesaleOrder,
  updateWholesaleOrder,
  voidWholesaleOrder,
  unshipWholesaleOrder,
  updateConnectorOrderAnnotations,
  updateConnectorLineAnnotations,
  deleteRetailOrder,
  listWholesaleCustomers,
  listTierSkus,
  getRetailOrder,
  markWholesaleOrderInvoiced,
  shipWholesaleOrder,
  downloadOrderSourcePdf
} from '../../services/retailOrdersService';
import { saveOrderDraft, getOrderDraft, deleteOrderDraft, updateFulfilmentDates } from './orderDraftsApi';
import { upsertPriceTierItems } from '../../services/inventoryService';
import api from '../../config/api';
import brandService from '../../services/brandService';
import { getWarehouses } from '../../services/inventoryService';
import { useSubsidiary } from '../../context/SubsidiaryContext';
import { PageHeader, StatusBadge } from '../ds';
import OrderPdfUpload from '../orders/OrderPdfUpload';
import { shouldApplyCustomerDefaults } from './customerDefaults';
import { estimateMargin } from './marginEstimate';

const WOE_API_BASE = process.env.REACT_APP_API_URL || 'https://api.superchargeme.com';

// Inline void-invoice call — same module-boundary reasoning as ReadyToInvoice.js.
// Clears the SCM-side invoice mark (qb_invoice_id/date, payment_status→pending,
// audited as 'void_invoice'); no inventory and no QuickBooks side effects.
async function voidWholesaleOrderInvoice(brandId, orderId) {
  const response = await api.post(
    `${WOE_API_BASE}/api/wholesale-orders/${brandId}/${orderId}/void-invoice`
  );
  return response.data;
}

/**
 * Wholesale SO entry — customer picker drives all auto-fills (tier, subsidiary,
 * terms, send method, default warehouse). Line-item SKU picker is bounded to
 * the customer's tier and auto-prices each row. Submit calls the P4.1 endpoint.
 */
// Selectable order labels/flags (Rishabh 2026-07-16). Stored comma-separated in
// retail_orders.tags. Pure metadata — no charge, no COGS, no P&L effect.
const ORDER_LABEL_OPTIONS = ['Gift Tag', 'Hoover'];

// DT-1453: a cost can be subtracted from a line price only when both are in the same
// currency. When the backend had no exchange rate it returns the cost unconverted
// (unit_cost_order_ccy_currency = the cost's own currency, e.g. USD) and a margin would
// subtract a USD cost from a MYR/CHF price. Returns { costCcy, priceCcy } in that case.
// A cost whose currency was never recorded cannot be compared with a known price currency
// either (it is not assumed USD), unless the cost is 0. Returns { costCcy: 'unknown currency', ... }.
function costCurrencyMismatch(item, orderCurrency) {
  const costCcy = String(item?.unit_cost_order_ccy_currency || '').toUpperCase();
  const priceCcy = String(item?.currency || orderCurrency || '').toUpperCase();
  if (!priceCcy) return null;
  if (!costCcy) {
    const cost = item?.unit_cost_order_ccy != null ? Number(item.unit_cost_order_ccy)
               : (item?.unit_cost != null ? Number(item.unit_cost) : null);
    return (cost != null && cost !== 0) ? { costCcy: 'unknown currency', priceCcy } : null;
  }
  return costCcy !== priceCcy ? { costCcy, priceCcy } : null;
}

export default function WholesaleOrderEntry() {
  const navigate = useNavigate();
  // Edit mode: /wholesale/new?edit=ORDER_ID&brand=BRAND_ID — the page becomes
  // a full-screen editor for that existing wholesale order. Falls back to
  // "create" when params are absent.
  const [searchParams] = useSearchParams();
  const editOrderId = searchParams.get('edit') || '';
  const editBrandId = searchParams.get('brand') || '';
  const isEditMode = Boolean(editOrderId && editBrandId);
  // Return to the FILTERED retail-orders list the operator came from — the list
  // stashes its filter query-string in sessionStorage (Rishabh 2026-07-02:
  // Back/Cancel/Save must land on the filtered list, not the whole list).
  const listBackTo = `/retail/orders${(() => { try { return sessionStorage.getItem('scm.retailListSearch') || ''; } catch (_) { return ''; } })()}`;
  // Draft mode: /wholesale/new?draft=DRAFT_ID&brand=BRAND_ID reopens a saved
  // draft (separate retail_order_drafts table — inert for all calculations).
  // Distinct from edit mode (a draft isn't a real order yet). draftId tracks the
  // in-progress draft so re-saving updates it and Finalize deletes it. Everything
  // draft-related is gated so the plain create/edit paths are untouched.
  const draftIdParam = searchParams.get('draft') || '';
  const draftMode = Boolean(draftIdParam && editBrandId && !isEditMode);
  const [draftId, setDraftId] = useState(draftIdParam || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLoading, setDraftLoading] = useState(draftMode);

  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [tierSkus, setTierSkus] = useState([]);
  const [pdfOpen, setPdfOpen] = useState(false);
  // Stored source-PDF reference (document-store relative path) — set by the PDF
  // prefill, carried into the save payload, hydrated back in edit/draft mode.
  const [sourceFile, setSourceFile] = useState(null);
  // Subsidiary picker — list from SubsidiaryContext, selection per-order.
  // Auto-fills from CWE on customer change; falls back to is_default (parent)
  // both on customer-change AND on initial subList-load (in case the operator
  // arrives at the form without picking a customer first).
  const { subsidiaries: subList } = useSubsidiary();
  const [subsidiaryId, setSubsidiaryId] = useState(0);
  useEffect(() => {
    if (subsidiaryId > 0) return;
    if (!subList || subList.length === 0) return;
    const def = subList.find(s => s.is_default && s.is_active !== false);
    if (def) setSubsidiaryId(Number(def.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subList]);
  // True while the edit fetch is in flight so we don't mount the form with
  // a half-hydrated state.
  const [editLoading, setEditLoading] = useState(isEditMode);
  // View-only — order is shipped/partially_shipped/invoiced/voided. Form loads
  // with all data populated, but Save is hidden and a "Revert latest shipment"
  // action is offered instead. fulfillmentStatus drives the banner copy.
  const [viewOnlyReason, setViewOnlyReason] = useState(null);
  // Finalized order (invoiced): view-only AND every mutating action suppressed
  // (no ship / invoice / revert), unlike a plain shipped order which can still
  // be invoiced or reverted from the view-only banner.
  const [actionsLocked, setActionsLocked] = useState(false);
  const [fulfillmentStatus, setFulfillmentStatus] = useState(null);
  const [revertOpen, setRevertOpen] = useState(false);
  const [revertReason, setRevertReason] = useState('');
  const [reverting, setReverting] = useState(false);
  // Un-invoice ("Remove invoice & reopen") — the way OUT of the invoiced lock.
  // Calls the existing void-invoice endpoint; without this button an invoiced
  // order was a dead-end (Rishabh, WHSM59-1: invoiced while still pending).
  const [unInvoiceOpen, setUnInvoiceOpen] = useState(false);
  const [unInvoicing, setUnInvoicing] = useState(false);
  const [isVoidedOrder, setIsVoidedOrder] = useState(false);
  // Two-stage fulfilment: Dispatch (goods leave WH → in_transit, stock deducts)
  // then Ship (records shipped date). Dispatch is set here on the order edit page.
  const [dispatchDate, setDispatchDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [savingFulfil, setSavingFulfil] = useState(false);
  // The order's saved fulfilment dates at load — Save diffs the editable fields
  // against these to tell a forward transition from a correction vs a clear.
  const [origFulfil, setOrigFulfil] = useState({ dispatch: '', shipped: '', invNum: '', invDate: '' });
  // Connector-imported orders (source NOT IN wholesale_native/cin7_wholesale)
  // get a dedicated read-only display rather than the locked-out Alert. Stores
  // the raw retail_orders row + parsed products_info. See ConnectorOrderView
  // render branch below.
  const [connectorOrder, setConnectorOrder] = useState(null);
  const [connectorAnnotations, setConnectorAnnotations] = useState({});
  const [connectorSaving, setConnectorSaving] = useState(false);
  // Destructive-action dialog state (visible only in edit mode).
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  // Track which ship-to variant the user picked (for retailers with multiple addresses)
  const [selectedVariantQbId, setSelectedVariantQbId] = useState(null);
  const customerChangedByUserRef = useRef(false);
  const [brandId, setBrandId] = useState('');
  const [warehouseCode, setWarehouseCode] = useState('');
  const [eventDate, setEventDate] = useState(() => new Date().toISOString().slice(0, 10));
  // expected_ship_date — operator's planned ship date. Used downstream by the
  // available-stock check to optionally include inbound inventory arriving
  // before this date (Phase 2). Defaults to 3 business days from order date.
  const [expectedShipDate, setExpectedShipDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [poNumber, setPoNumber] = useState('');
  const [labels, setLabels] = useState([]); // order labels/flags → retail_orders.tags
  const [shippingAddress, setShippingAddress] = useState('');
  const [orderNotes, setOrderNotes] = useState(''); // internal team notes
  const [customerNotes, setCustomerNotes] = useState(''); // shown on invoice to customer
  const [deliveryInstructions, setDeliveryInstructions] = useState(''); // separate from notes
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  // Tax: numeric percent (e.g. 20 = 20%). Auto-fills parsed from CWE.tax_rate text.
  const [taxPercent, setTaxPercent] = useState('');
  const [taxLabel, setTaxLabel] = useState('');
  // Region-aware tax terminology — UK/EU say "VAT", AU/NZ/SG say "GST", else "Tax".
  // Derived below from delivery country (preferred) or ship-from warehouse country.

  // Tax calculation mode (CIN7 parity):
  //   'exclusive' (default) — line prices are pre-tax, tax added on top
  //   'inclusive' — line prices INCLUDE tax; tax is shown as "of which tax: X" for clarity
  //   'exempt' — no tax line (forces taxPct to 0 for the calc)
  const [taxMode, setTaxMode] = useState('exclusive');
  // Freight + surcharge as separate fields (matches CIN7's Total Freight / Surcharge rows)
  const [freightAmount, setFreightAmount] = useState(0);
  const [surchargeAmount, setSurchargeAmount] = useState(0);
  // Structured addresses (delivery + billing), auto-filled from CWE
  const [delivery, setDelivery] = useState({
    company: '', first_name: '', last_name: '',
    address_1: '', address_2: '', city: '', state: '', postal_code: '', country: '',
    email: '', phone: ''
  });
  const [billing, setBilling] = useState({
    company: '', address_1: '', city: '', state: '', postal_code: '', country: ''
  });
  // Multi-address: the selected customer's saved alternate ship-to addresses,
  // plus which one is chosen (0 = Primary / customer-master delivery address).
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [shipToId, setShipToId] = useState(0);
  // On edit, the order's stored flat ship-to / bill-to blobs. address_1 + company
  // aren't stored structured, so an edit that doesn't re-type a street address
  // preserves these verbatim instead of overwriting with a partial rebuild.
  const [origShippingAddr, setOrigShippingAddr] = useState('');
  const [origBillingAddr, setOrigBillingAddr] = useState('');
  const [discountAmount, setDiscountAmount] = useState(0);

  // Line items:
  //   quantity_ordered  = what the retailer asked for
  //   quantity_dispatched = what we'll actually ship (≤ ordered when stock is short)
  //   Tax + totals are computed off quantity_dispatched since that's what's invoiced.
  // SKU currently being added to the customer's price tier (CHANGE 3) — disables
  // that line's "Add to tier" button while the request is in flight.
  const [addingTierSku, setAddingTierSku] = useState(null);
  const [lines, setLines] = useState([{
    product_sku: '', quantity_ordered: 1, quantity_dispatched: 1,
    unit_price: '', override: false, is_gift: false, hs_code: '', country_of_origin: ''
  }]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // `success` is for "order created/updated" — shows the jump buttons to
  // Pending Ship / Ready-to-Invoice. `notice` is for incidental info messages
  // (brand changed, lines reset, etc.) — no jump buttons.
  const [success, setSuccess] = useState(null);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [c, b, w] = await Promise.all([
          listWholesaleCustomers(),
          brandService.getBrands(),
          getWarehouses({ active_only: true })
        ]);
        setCustomers(c.data || []);
        // brandService.getBrands() returns an array; exclude the synthetic
        // "Company Level" brand (id=1, database=company_db) which isn't a real
        // sales brand — wholesale orders need a real brand DB to land in.
        const brandsList = Array.isArray(b) ? b : (b.data || b.brands || []);
        setBrands(brandsList.filter(x => x && x.id !== 1 && x.database && x.database !== ''));
        // /api/warehouses returns { success, warehouses, total } — the page
        // crashed earlier because we treated the wrapper as an array. Pick the
        // actual array out, then keep only own-type active warehouses (we
        // ship FROM our own warehouses, not from supplier facilities).
        const whList = Array.isArray(w) ? w : (w.warehouses || w.data || []);
        setWarehouses(whList.filter(x => x && x.is_active === 1 && (x.warehouse_type === 'own' || !x.warehouse_type)));
      } catch (e) {
        setError(e.response?.data?.error || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  // EDIT MODE — once customers + brands are loaded, fetch the existing order
  // and hydrate the form. Runs only when ?edit=ORDER_ID&brand=BID is present.
  // Customer selection is matched by qb_customer_id (the order's customer_id
  // column). Line items round-trip through the same products_info shape; legacy
  // orders without quantity_dispatched fall back to quantity for both fields.
  useEffect(() => {
    if (!isEditMode) return;
    if (loading || customers.length === 0 || brands.length === 0) return;
    (async () => {
      try {
        setEditLoading(true);
        const r = await getRetailOrder(parseInt(editBrandId), editOrderId);
        const ord = r?.data || r;
        if (!ord) throw new Error('Order not found');
        // Resolve the order's customer -> CWE tier group and select it, so the
        // customer's price tier loads (tierSkus) and the Tier Price column resolves.
        // The connector read-only view RETURNS below before the editable path's own
        // resolver, so it must call this itself — else portal orders never resolve a
        // tier and Tier Price shows "-". Same name-stem match as the editable path
        // (incl. the legal-entity-suffix strip so "Flaconi GmbH" -> CWE "Flaconi").
        const resolveCustomerGroup = () => {
          let matchVariant = customers.find(c => c.qb_customer_id === ord.customer_id);
          if (!matchVariant && ord.customer_name) {
            const LEGAL_SUFFIX = /\s+(gmbh|mbh|ag|kg|ug|ohg|gbr|co|ltd|limited|llc|inc|incorporated|corp|corporation|company|plc|pte|pty|bv|nv|sarl|sa|srl|spa|sl|sas|oy|ab|as|aps|kk|pvt|holdings?)$/i;
            const custStem = (n) => {
              let s = String(n || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0]
                .replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();
              let prev;
              do { prev = s; s = s.replace(LEGAL_SUFFIX, '').trim(); } while (s !== prev && s.length > 0);
              return s.toLowerCase();
            };
            const orderStem = custStem(ord.customer_name);
            if (orderStem.length >= 3) matchVariant = customers.find(c => custStem(c.customer_name) === orderStem);
          }
          if (!matchVariant) return;
          const stem = String(matchVariant.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
          setSelectedCustomer({
            canonical: stem,
            tier_name: matchVariant.tier_name,
            currency: matchVariant.currency,
            variants: customers.filter(c => {
              const s = String(c.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
              return s === stem && c.tier_name === matchVariant.tier_name;
            }),
            primary: matchVariant,
          });
          setSelectedVariantQbId(matchVariant.qb_customer_id);
        };
        if (!['wholesale_native', 'cin7_wholesale'].includes(ord.source)) {
          // Connector-imported (thg_procure, future ones) — we don't own this
          // order's lifecycle (THG portal / external system is source-of-truth),
          // but we DO want to inspect what was received. Render a dedicated
          // read-only view with the line-level fulfilment progress that THG's
          // header-level status hides. No edit affordance — edits here wouldn't
          // propagate back to the upstream system.
          // Backend returns products_info already JSON-parsed (see retail-orders-
          // controller.js#getRetailOrder line 295). Don't re-parse — just normalize
          // to an array. Older code paths or external callers might still pass a
          // string, so handle both shapes.
          let parsedLines = [];
          if (Array.isArray(ord.products_info)) {
            parsedLines = ord.products_info;
          } else if (typeof ord.products_info === 'string' && ord.products_info.trim().length > 0) {
            try {
              const v = JSON.parse(ord.products_info);
              if (Array.isArray(v)) parsedLines = v;
            } catch (_) { parsedLines = []; }
          }
          setConnectorOrder({ ...ord, _lines: parsedLines });
          setConnectorAnnotations({
            order_notes: ord.order_notes || '',
            tags: ord.tags || '',
            warehouse_code: ord.warehouse_code || '',
            tracking_number: ord.tracking_number || '',
            carrier: ord.carrier || '',
          });
          // Status / voided / closed drive the "Close order: rest won't ship" control.
          setFulfillmentStatus(ord.fulfillment_status);
          if (ord.is_voided === 1) setIsVoidedOrder(true);
          setClosedRestWontShip(!!ord.closed_rest_wont_ship);
          resolveCustomerGroup();
          return;
        }
        // Final / non-editable states load READ-ONLY (the form is fully populated
        // so the operator can inspect everything) instead of slamming shut on a
        // bare warning. VOIDED and INVOICED orders additionally lock every
        // mutating action (actionsLocked); a plain shipped/partially_shipped order
        // stays view-only but can still be invoiced / reverted from the banner.
        // (Reported by Rishabh: invoiced — and then voided — orders hid all details.)
        setFulfillmentStatus(ord.fulfillment_status);
        // Populate the fulfilment date fields from the saved order so the single
        // Save diffs against the real values (and doesn't clobber a real dispatch
        // date with today's default). Dates are Nullable in the DB → '' when unset.
        const odDispatch = ord.dispatch_date ? String(ord.dispatch_date).slice(0, 10) : '';
        const odShipped = ord.shipped_date ? String(ord.shipped_date).slice(0, 10) : '';
        const odInvNum = ord.qb_invoice_id || '';
        const odInvDate = ord.qb_invoice_date ? String(ord.qb_invoice_date).slice(0, 10) : '';
        setDispatchDate(odDispatch);
        if (odShipped) setShippedDateInput(odShipped);
        if (odInvNum) setInvoiceNumberInput(odInvNum);
        if (odInvDate) setInvoiceDateInput(odInvDate);
        setOrigFulfil({ dispatch: odDispatch, shipped: odShipped, invNum: odInvNum, invDate: odInvDate });
        setClosedRestWontShip(!!ord.closed_rest_wont_ship);
        if (ord.is_voided === 1) {
          setViewOnlyReason('This order is voided — view only.');
          setActionsLocked(true);
          setIsVoidedOrder(true);
        } else if (ord.fulfillment_status !== 'pending') {
          setViewOnlyReason(`Order is ${ord.fulfillment_status} — line items are locked. You can still update the dispatch / delivered / invoice details below and Save. To edit the lines, clear the Dispatch date first.`);
        }
        // An invoice reference does NOT lock the order — SCM doesn't create or
        // sync QuickBooks invoices, so qb_invoice_id is a reference, not a
        // state (Rishabh 2026-07-02). It renders as its own info banner and is
        // editable/removable from the Fulfilment section / banner action.

        // Brand
        setBrandId(parseInt(editBrandId));
        // Find the matching customer group. Two paths:
        //  1. Direct match on qb_customer_id (new wholesale_native orders that
        //     used the modern CWE customer-id space).
        //  2. Name-stem fallback (CIN7-imported orders whose customer_id is in
        //     the OLD CIN7 sales-order space and doesn't appear in CWE — e.g.,
        //     retail_orders has CIN7-2584 but CWE has CIN7-52 for Flaconi).
        let matchVariant = customers.find(c => c.qb_customer_id === ord.customer_id);
        if (!matchVariant && ord.customer_name) {
          // Name-stem fallback: strip parentheticals, " - " suffixes, AND trailing
          // legal-entity suffixes (GmbH / Ltd / Inc / Pte Ltd / …) so a portal name
          // like "Flaconi GmbH" matches the CWE record "Flaconi". Without the legal-
          // suffix strip the customer never resolves for connector orders, so the
          // tier (and its price-check column) shows "—" even when the tier is set.
          // Trailing legal token; dots/commas are dropped first so dotted EU forms
          // (S.L. / S.A. / B.V.) collapse to SL / SA / BV and strip cleanly too.
          const LEGAL_SUFFIX = /\s+(gmbh|mbh|ag|kg|ug|ohg|gbr|co|ltd|limited|llc|inc|incorporated|corp|corporation|company|plc|pte|pty|bv|nv|sarl|sa|srl|spa|sl|sas|oy|ab|as|aps|kk|pvt|holdings?)$/i;
          const custStem = (n) => {
            let s = String(n || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0]
              .replace(/[.,]/g, '').replace(/\s+/g, ' ').trim();
            let prev;
            do { prev = s; s = s.replace(LEGAL_SUFFIX, '').trim(); } while (s !== prev && s.length > 0);
            return s.toLowerCase();
          };
          const orderStem = custStem(ord.customer_name);
          if (orderStem.length >= 3) {
            matchVariant = customers.find(c => custStem(c.customer_name) === orderStem);
          }
        }
        if (matchVariant) {
          const stem = String(matchVariant.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
          const grp = {
            canonical: stem,
            tier_name: matchVariant.tier_name,
            currency: matchVariant.currency,
            variants: customers.filter(c => {
              const s = String(c.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
              return s === stem && c.tier_name === matchVariant.tier_name;
            }),
            primary: matchVariant,
          };
          setSelectedCustomer(grp);
          setSelectedVariantQbId(matchVariant.qb_customer_id);
        }

        // Simple scalars
        setWarehouseCode(ord.warehouse_code || '');
        if (ord.event_date) setEventDate(String(ord.event_date).slice(0, 10));
        if (ord.expected_ship_date) setExpectedShipDate(String(ord.expected_ship_date).slice(0, 10));
        setPoNumber(ord.po_number || '');
        setTrackingNumber(ord.tracking_number || '');
        setCarrier(ord.carrier || '');
        setOrderNotes(ord.order_notes || '');
        setLabels(ord.tags ? String(ord.tags).split(',').map(s => s.trim()).filter(Boolean) : []);
        setCustomerNotes(ord.customer_notes || '');
        setDeliveryInstructions(ord.delivery_instructions || '');
        // Remember the stored flat blobs so an untouched save preserves them
        // verbatim (the flat address is the source of truth).
        setOrigShippingAddr(ord.shipping_address || '');
        setOrigBillingAddr(ord.billing_address || '');
        // Ship-to on edit: if the order has its own ship-to, show the structured
        // columns it stores (city/state/zip/country/contact; address_1+company
        // aren't stored structured so they stay blank and the stored blob is
        // preserved on save). If the order has NO ship-to, auto-fill from the
        // customer master so the operator doesn't have to retype it (Rishabh
        // 2026-07-20 — "why is it not auto fetching from customer master data?").
        const orderHasShip = !!(String(ord.shipping_address || '').trim() || String(ord.shipping_city || '').trim());
        // Street/company aren't stored as columns (only inside the flat blob). If
        // the order's stored blob actually CONTAINS the master's street, the order
        // was shipped to the master address, so it's safe to show the master's
        // street/company AND to rebuild on save (round-trips to the same blob). If
        // it doesn't (a custom / picked ship-to), we leave the street blank and
        // preserve the stored blob verbatim — never overwrite a custom address.
        const masterStreet = String(matchVariant?.delivery_address_1 || '').trim();
        const shipMatchesMaster = !!(matchVariant && masterStreet
          && String(ord.shipping_address || '').toLowerCase().includes(masterStreet.toLowerCase()));
        if (orderHasShip) {
          setDelivery(d => ({
            ...d,
            company: shipMatchesMaster ? (matchVariant.customer_name || '') : '',
            first_name: (ord.delivery_contact || '').trim().split(/\s+/)[0] || '',
            last_name: (ord.delivery_contact || '').trim().split(/\s+/).slice(1).join(' ') || '',
            address_1: shipMatchesMaster ? (matchVariant.delivery_address_1 || '') : '',
            address_2: shipMatchesMaster ? (matchVariant.delivery_address_2 || '') : '',
            city: ord.shipping_city || '',
            state: ord.shipping_state || '',
            postal_code: ord.shipping_zip || '',
            country: ord.shipping_country_code || '',
            email: shipMatchesMaster ? (matchVariant.contact_email || '') : '',
            phone: ord.delivery_phone || (shipMatchesMaster ? (matchVariant.contact_phone || '') : ''),
          }));
        } else if (matchVariant) {
          setDelivery(d => ({
            ...d,
            company: matchVariant.customer_name || '',
            first_name: matchVariant.contact_first_name || '',
            last_name: matchVariant.contact_last_name || '',
            address_1: matchVariant.delivery_address_1 || '',
            address_2: matchVariant.delivery_address_2 || '',
            city: matchVariant.delivery_city || '',
            state: matchVariant.delivery_state || '',
            postal_code: matchVariant.delivery_postal_code || '',
            country: matchVariant.delivery_country || '',
            email: matchVariant.contact_email || '',
            phone: matchVariant.contact_phone || '',
          }));
        }
        // Bill-to on edit: there are no structured billing columns at all, so we
        // fill from the customer master when the order has NO billing OR when its
        // stored billing blob contains the master's billing street (order billed
        // to the master address → safe to show + round-trip). A custom billing
        // address (blob present but not the master's) stays blank and is
        // preserved verbatim on save via origBillingAddr.
        const masterBillStreet = String(matchVariant?.billing_address_1 || matchVariant?.delivery_address_1 || '').trim();
        const billMatchesMaster = !!(matchVariant && masterBillStreet
          && String(ord.billing_address || '').toLowerCase().includes(masterBillStreet.toLowerCase()));
        if (matchVariant && (!String(ord.billing_address || '').trim() || billMatchesMaster)) {
          setBilling(b => ({
            ...b,
            company: matchVariant.customer_name || '',
            address_1: matchVariant.billing_address_1 || matchVariant.delivery_address_1 || '',
            city: matchVariant.billing_city || matchVariant.delivery_city || '',
            state: matchVariant.billing_state || matchVariant.delivery_state || '',
            postal_code: matchVariant.billing_postal_code || matchVariant.delivery_postal_code || '',
            country: matchVariant.billing_country || matchVariant.delivery_country || '',
          }));
        }
        setDiscountAmount(Number(ord.discount_amount) || 0);
        setFreightAmount(Number(ord.shipping_price) || 0);
        if (ord.subsidiary_id && Number(ord.subsidiary_id) > 0) {
          setSubsidiaryId(Number(ord.subsidiary_id));
        }
        setSurchargeAmount(Number(ord.surcharge) || 0);
        setSourceFile(typeof ord.source_file === 'string' && ord.source_file.startsWith('order-pdfs/') ? ord.source_file : null);

        // Tax — stored as decimal fraction (0.2), form holds percent (20)
        const taxRateDecimal = Number(ord.tax_rate) || 0;
        setTaxPercent(taxRateDecimal > 0 ? String(taxRateDecimal * 100) : '');
        setTaxMode(ord.tax_status === 'Incl' ? 'inclusive'
          : ord.tax_status === 'Exempt' ? 'exempt' : 'exclusive');

        // Lines
        let parsedLines = [];
        try {
          const raw = typeof ord.products_info === 'string'
            ? JSON.parse(ord.products_info) : (ord.products_info || []);
          parsedLines = (Array.isArray(raw) ? raw : []).map(p => {
            const qo = Number(p.quantity_ordered ?? p.quantity ?? 0);
            const qd = Number(p.quantity_dispatched ?? p.quantity ?? qo);
            // A persisted give-away carries an explicit is_gift flag. Preserve the 0
            // verbatim — the old `Number(p.unit_price) || ''` collapsed a £0 gift back
            // to a blank line, which would have re-priced it from the tier on the next
            // save.
            // is_gift is the ONLY signal here, mirroring the write path. An earlier
            // draft also inferred a gift from `price_source === 'override' && price === 0`,
            // which re-imported on the READ path exactly the value-inference the write
            // path removed. It was dead weight — the backend only ever emits
            // price_source 'override' when override !== null, and the non-gift branch
            // requires unit_price > 0, so override+0 implies is_gift was already true
            // (verified: zero rows in production match that shape). Left in, it would
            // silently mis-flag a line as a give-away the day any writer emits
            // price_source 'override' with a 0.
            const hasPrice = p.unit_price !== '' && p.unit_price != null;
            const isGift = p.is_gift === true;
            return {
              product_sku: p.product_sku || '',
              quantity_ordered: qo,
              quantity_dispatched: qd,
              unit_price: hasPrice ? Number(p.unit_price) : '',
              override: isGift || p.price_source === 'override',
              is_gift: isGift,
              hs_code: p.hs_code || '',
              country_of_origin: p.country_of_origin || '',
            };
          });
        } catch (_) { parsedLines = []; }
        if (parsedLines.length > 0) setLines(parsedLines);
      } catch (e) {
        setError(`Failed to load order: ${e.response?.data?.error || e.message}`);
      } finally {
        setEditLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, loading, customers.length, brands.length]);

  // Draft reopen: rehydrate the form from a saved draft's draft_payload. Mirrors
  // the edit-mode loader but reads retail_order_drafts (not a real order). We
  // restore the customer/brand/lines/scalars; the customer-change effect then
  // re-applies that customer's default warehouse/tax/address (same as edit mode).
  useEffect(() => {
    if (!draftMode) return;
    if (loading || customers.length === 0 || brands.length === 0) return;
    (async () => {
      try {
        setDraftLoading(true);
        const r = await getOrderDraft(parseInt(editBrandId), draftIdParam);
        const d = r?.draft || r;
        let fs = {};
        try { fs = typeof d.draft_payload === 'string' ? JSON.parse(d.draft_payload) : (d.draft_payload || {}); }
        catch (_) { fs = {}; }
        setDraftId(d.draft_id || draftIdParam);
        setBrandId(parseInt(editBrandId));

        // Re-select the customer group (same matching logic as edit mode).
        if (fs.selectedCustomerQbId) {
          const mv = customers.find(c => c.qb_customer_id === fs.selectedCustomerQbId);
          if (mv) {
            const stem = String(mv.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
            setSelectedCustomer({
              canonical: stem, tier_name: mv.tier_name, currency: mv.currency,
              variants: customers.filter(c => {
                const s = String(c.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
                return s === stem && c.tier_name === mv.tier_name;
              }),
              primary: mv,
            });
            if (fs.selectedVariantQbId) setSelectedVariantQbId(fs.selectedVariantQbId);
          }
        }

        if (fs.warehouseCode) setWarehouseCode(fs.warehouseCode);
        if (fs.eventDate) setEventDate(String(fs.eventDate).slice(0, 10));
        if (fs.expectedShipDate) setExpectedShipDate(String(fs.expectedShipDate).slice(0, 10));
        if (fs.poNumber != null) setPoNumber(fs.poNumber);
        if (fs.trackingNumber != null) setTrackingNumber(fs.trackingNumber);
        if (fs.carrier != null) setCarrier(fs.carrier);
        if (fs.orderNotes != null) setOrderNotes(fs.orderNotes);
        if (fs.customerNotes != null) setCustomerNotes(fs.customerNotes);
        if (fs.deliveryInstructions != null) setDeliveryInstructions(fs.deliveryInstructions);
        if (fs.taxPercent != null && fs.taxPercent !== '') setTaxPercent(fs.taxPercent);
        if (fs.taxMode) setTaxMode(fs.taxMode);
        if (fs.discountAmount != null) setDiscountAmount(fs.discountAmount);
        if (fs.freightAmount != null) setFreightAmount(fs.freightAmount);
        if (fs.surchargeAmount != null) setSurchargeAmount(fs.surchargeAmount);
        if (fs.subsidiaryId) setSubsidiaryId(Number(fs.subsidiaryId) || 0);
        if (Array.isArray(fs.lines) && fs.lines.length > 0) setLines(fs.lines);
        if (fs.sourceFile) setSourceFile(fs.sourceFile);
      } catch (e) {
        setError(`Failed to load draft: ${e.response?.data?.error || e.message}`);
      } finally {
        setDraftLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftMode, loading, customers.length, brands.length]);

  // Pre-select Brand from ?brand=ID even in CREATE mode (when not editing).
  // Lets the "+ Add New Order" button on /retail/orders hand off the
  // currently-selected brand context without an extra click. Only runs once
  // brands are loaded and only if we're NOT in edit mode (edit-mode loader
  // already sets brandId from the order's stored brand).
  useEffect(() => {
    if (isEditMode) return;
    if (brands.length === 0) return;
    if (!editBrandId) return;
    const wantId = parseInt(editBrandId);
    if (brandId === wantId) return;
    if (brands.some(b => b.id === wantId)) setBrandId(wantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editBrandId, brands.length]);

  // Group customers by canonical name. Strip parenthetical suffixes FIRST
  // ("Flaconi (Teresa Hamacher)" → "Flaconi"; "The Hut.com Limited UK (THG - UK)"
  // → "The Hut.com Limited UK") because the parens often contain a contact-name
  // or sub-org that would otherwise pollute the picker. Then split on " - " to
  // pull off the ship-to variant ("Myer - 460" → "Myer"). Same tier_name +
  // same canonical = same retailer; variants surface as a sub-picker.
  const customerGroups = useMemo(() => {
    const groups = new Map();
    for (const c of customers) {
      const stem = String(c.customer_name || c.display_name || c.qb_customer_id)
        .replace(/\s*\([^)]*\)/g, '')
        .split(' - ')[0]
        .trim();
      const key = `${stem}__${c.tier_name || ''}`;
      if (!groups.has(key)) {
        groups.set(key, { canonical: stem, tier_name: c.tier_name, currency: c.currency, variants: [], primary: c });
      }
      groups.get(key).variants.push(c);
    }
    return Array.from(groups.values()).map(g => {
      // Pick a sensible primary: prefer one with non-empty delivery_country
      const withAddr = g.variants.find(v => v.delivery_country) || g.variants[0];
      return { ...g, primary: withAddr };
    }).sort((a, b) => a.canonical.localeCompare(b.canonical));
  }, [customers]);

  // The "active variant" = whatever ship-to the user picked, or the group's primary
  const activeCustomer = useMemo(() => {
    if (!selectedCustomer) return null;
    if (!selectedVariantQbId) return selectedCustomer.primary;
    return selectedCustomer.variants.find(v => v.qb_customer_id === selectedVariantQbId) || selectedCustomer.primary;
  }, [selectedCustomer, selectedVariantQbId]);

  // Reset variant selection when group changes; default to first variant with an address
  useEffect(() => {
    if (!selectedCustomer) { setSelectedVariantQbId(null); return; }
    setSelectedVariantQbId(selectedCustomer.primary?.qb_customer_id || null);
  }, [selectedCustomer]);

  // Multi-address: load the active customer's saved alternate ship-to addresses
  // and reset the picker to Primary (0) whenever the customer changes.
  useEffect(() => {
    const cid = activeCustomer?.cwe_id;
    setShipToId(0);
    if (!cid) { setCustomerAddresses([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`${WOE_API_BASE}/api/wholesale-orders/customers/${cid}/addresses`);
        if (!cancelled) setCustomerAddresses(r.data?.data || []);
      } catch { if (!cancelled) setCustomerAddresses([]); }
    })();
    return () => { cancelled = true; };
  }, [activeCustomer?.cwe_id]);

  // Apply a chosen ship-to onto the structured delivery fields. id 0 = Primary
  // (restore from the customer master); otherwise pull the picked address row.
  const applyShipTo = (id) => {
    setShipToId(id);
    if (!id) {
      const ac = activeCustomer;
      if (!ac) return;
      setDelivery(d => ({
        ...d,
        company: ac.customer_name || '',
        first_name: ac.contact_first_name || '',
        last_name: ac.contact_last_name || '',
        address_1: ac.delivery_address_1 || '',
        address_2: ac.delivery_address_2 || '',
        city: ac.delivery_city || '',
        state: ac.delivery_state || '',
        postal_code: ac.delivery_postal_code || '',
        country: ac.delivery_country || '',
        email: ac.contact_email || '',
        phone: ac.contact_phone || '',
      }));
      return;
    }
    const a = customerAddresses.find(x => x.id === id);
    if (!a) return;
    setDelivery(d => ({
      ...d,
      first_name: a.contact_name || d.first_name,
      last_name: a.contact_name ? '' : d.last_name,
      address_1: a.address_1 || '',
      address_2: a.address_2 || '',
      city: a.city || '',
      state: a.state || '',
      postal_code: a.postal_code || '',
      country: a.country || '',
      phone: a.phone || d.phone,
    }));
  };

  // When activeCustomer (group + variant) changes, apply customer defaults.
  // Do not run this during edit/draft hydration: existing orders/drafts carry
  // their own tax/warehouse/address values, and customer-master defaults can be
  // stale or blank (e.g. Pricecheck VAT manually set to 20%).
  useEffect(() => {
    if (!activeCustomer) {
      setTaxPercent(''); setTaxLabel('');
      setShippingAddress('');
      return;
    }
    // Use activeCustomer (group + variant) everywhere downstream.
    // Alias for readability — all the existing field reads against `selectedCustomer`
    // now resolve against the variant the user picked.
    const ac = activeCustomer;
    if (shouldApplyCustomerDefaults({
      isExistingRecordMode: isEditMode || draftMode,
      customerChangedByUser: customerChangedByUserRef.current,
    })) {
      // Default warehouse: explicit CWE override → geography fallback by delivery country
      // Subsidiary: prefer CWE, else fall back to the company's is_default
      // subsidiary from SubsidiaryContext (the "parent" — no hardcoding).
      if (ac.subsidiary_id && Number(ac.subsidiary_id) > 0) {
        setSubsidiaryId(Number(ac.subsidiary_id));
      } else {
        const defaultSub = (subList || []).find(s => s.is_default);
        setSubsidiaryId(defaultSub ? Number(defaultSub.id) : 0);
      }
      if (ac.default_warehouse_code) {
        setWarehouseCode(ac.default_warehouse_code);
      } else {
        const country = String(ac.delivery_country || '').toLowerCase();
        // Heuristic country → warehouse mapping (mirrors cin7_port_config.country_prefix_map intent)
        const geoMap = {
          'australia': 'G3PL',
          'united kingdom': 'SYN', 'uk': 'SYN',
          'united states': 'ITC', 'usa': 'ITC',
          'singapore': 'IPS', 'malaysia': 'IPS',
          'germany': 'OMN', 'netherlands': 'OMN', 'france': 'OMN', 'italy': 'OMN',
          'spain': 'OMN', 'belgium': 'OMN', 'austria': 'OMN', 'poland': 'BT',
          'switzerland': 'OMN', 'czech republic': 'OMN', 'sweden': 'OMN',
        };
        const guess = geoMap[country];
        if (guess) setWarehouseCode(guess);
      }
      // Tax: parse the CWE.tax_rate text. Audit (2026-05-14) found 8 distinct
      // values across the customer master — handle each:
      //   "20%"                        → 20
      //   "Zero Rated EC Goods Income" → 0
      //   "Zero-Rated Supplies"        → 0
      //   "Tax Exempt"                 → 0
      //   "GST on Income"              → 10   (Australian GST default — 12 customers)
      //   "Standard-Rated Supplies"    → 20   (UK VAT standard — 1 customer)
      //   "Tax on Sales"               → ''   (US use-tax — country-dependent, manual)
      //   "" or null                   → ''
      const raw = String(ac.tax_rate || '');
      const lower = raw.toLowerCase().trim();
      const pctMatch = raw.match(/(\d+(?:\.\d+)?)\s*%/);
      let parsed = '';
      if (pctMatch) {
        parsed = pctMatch[1];
      } else if (lower.includes('zero') || lower.includes('exempt')) {
        parsed = '0';
      } else if (lower.includes('gst')) {
        parsed = '10'; // AU/SG GST baseline; user can override on the line if needed
      } else if (lower.includes('standard') && lower.includes('rated')) {
        parsed = '20'; // UK VAT standard rate
      }
      setTaxPercent(parsed);
      setTaxLabel(raw);
      // Auto-populate structured delivery + billing from CWE
      setDelivery({
        company: ac.customer_name || '',
        first_name: ac.contact_first_name || '',
        last_name: ac.contact_last_name || '',
        address_1: ac.delivery_address_1 || '',
        address_2: ac.delivery_address_2 || '',
        city: ac.delivery_city || '',
        state: ac.delivery_state || '',
        postal_code: ac.delivery_postal_code || '',
        country: ac.delivery_country || '',
        email: ac.contact_email || '',
        phone: ac.contact_phone || '',
      });
      setBilling({
        company: ac.customer_name || '',
        address_1: ac.billing_address_1 || ac.delivery_address_1 || '',
        city: ac.billing_city || ac.delivery_city || '',
        state: ac.billing_state || ac.delivery_state || '',
        postal_code: ac.billing_postal_code || ac.delivery_postal_code || '',
        country: ac.billing_country || ac.delivery_country || '',
      });
      // Also keep the flat shipping_address field as a fallback for the backend.
      const deliveryParts = [
        ac.delivery_address_1,
        ac.delivery_address_2,
        [ac.delivery_city, ac.delivery_state, ac.delivery_postal_code]
          .filter(Boolean).join(', '),
        ac.delivery_country,
      ].filter(Boolean);
      if (deliveryParts.length > 0) setShippingAddress(deliveryParts.join('\n'));
    }
  }, [activeCustomer, isEditMode, draftMode, subList]);

  // Refresh tier SKUs whenever customer/brand/warehouse/ship date changes. This
  // must not reapply customer defaults, or editing an expected ship date can
  // wipe manually-entered VAT/warehouse/address values.
  useEffect(() => {
    if (!activeCustomer) {
      setTierSkus([]);
      return;
    }
    const ac = activeCustomer;
    (async () => {
      // A customer with NO price tier (one-off / ad-hoc retailer) still needs to
      // pick SKUs and type prices manually. listTierSkus returns the full brand
      // catalogue (unpriced / Shopify-fallback) for any tier with zero rules;
      // passing tierId=0 reaches that same fallback, so a tier-less customer
      // loads the catalogue instead of an empty picker. That fallback needs a
      // brand to resolve the catalogue — with neither tier nor brand there's
      // nothing to list, so skip the call (don't error).
      const tierId = ac.price_tier_id || 0;
      if (!tierId && !brandId) {
        setTierSkus([]);
        return;
      }
      try {
        // Brand-scoped + warehouse-aware: each SKU also returns
        // available_quantity at the chosen warehouse so the line items can
        // display "Available: N" in italics. When expectedShipDate is also set,
        // each SKU additionally carries inbound_quantity (open POs + in-transit
        // transfers due by that date at warehouseCode). Refetches whenever
        // brand, warehouse, or ship date changes.
        const r = await listTierSkus(
          tierId,
          brandId || null,
          warehouseCode || null,
          expectedShipDate || null
        );
        setTierSkus(r.data || []);
      } catch (e) {
        setError(`Failed to load SKUs: ${e.response?.data?.error || e.message}`);
      }
    })();
  }, [activeCustomer, brandId, warehouseCode, expectedShipDate]);

  // Brand-change guard — when the user switches the Brand selector while there
  // are line items already added, those lines' SKUs likely belong to a
  // different brand and would be silently orphaned. Surface a warning and
  // reset to a single blank line so the user re-picks from the new brand pool.
  const prevBrandRef = useRef(brandId);
  useEffect(() => {
    if (prevBrandRef.current && prevBrandRef.current !== brandId) {
      const hasLines = lines.some(l => l.product_sku);
      if (hasLines) {
        setLines([{ product_sku: '', quantity_ordered: 1, quantity_dispatched: 1, unit_price: '', override: false, is_gift: false, hs_code: '', country_of_origin: '' }]);
        setNotice(`Brand changed — cleared previous line items. Re-pick SKUs from the new brand.`);
      }
    }
    prevBrandRef.current = brandId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  // Region-aware tax terminology — UK + EU → "VAT", AU + NZ + Singapore → "GST",
  // else "Tax". Customer's tax_rate text wins (e.g. "GST on Income" forces GST
  // regardless of country); falls back to the delivery country, then warehouse
  // country. Affects DISPLAY ONLY — the math uses taxPercent unchanged.
  const taxTerm = useMemo(() => {
    const rate = (activeCustomer?.tax_rate || '').toLowerCase();
    if (rate.includes('gst')) return 'GST';
    if (rate.includes('vat')) return 'VAT';
    const wh = warehouses.find(w => w.code === warehouseCode);
    const country = (delivery.country || wh?.country || '').trim().toLowerCase();
    const VAT_REGIONS = new Set([
      'united kingdom','uk','gb','great britain','england','scotland','wales','northern ireland',
      'germany','de','france','fr','italy','it','spain','es','netherlands','nl','belgium','be',
      'denmark','dk','sweden','se','finland','fi','ireland','ie','portugal','pt','austria','at',
      'czech republic','cz','poland','pl','hungary','hu','romania','ro','greece','gr','luxembourg','lu',
      'slovenia','si','slovakia','sk','estonia','ee','latvia','lv','lithuania','lt',
      'bulgaria','bg','croatia','hr','cyprus','cy','malta','mt'
    ]);
    const GST_REGIONS = new Set(['australia','au','aus','new zealand','nz','singapore','sg']);
    if (VAT_REGIONS.has(country)) return 'VAT';
    if (GST_REGIONS.has(country)) return 'GST';
    return 'Tax';
  }, [activeCustomer, delivery.country, warehouseCode, warehouses]);

  // Hydrate the SO form from a parsed PDF prefill payload. Matched lines arrive
  // with a resolved product_sku; low-confidence lines arrive with '' (flagged in
  // the upload preview) so the operator picks them in the tier SKU picker. The
  // PDF's price is kept (override=true) so it isn't wiped by tier auto-pricing;
  // the operator can clear override to fall back to tier price.
  const applyPdfPrefill = (prefill) => {
    const o = prefill.order || {};
    if (prefill.source_file) setSourceFile(prefill.source_file);
    if (o.po_number) setPoNumber(o.po_number);
    if (o.shipping_address) setShippingAddress(o.shipping_address);
    if (o.order_notes) setOrderNotes(o.order_notes);
    if (o.event_date) setEventDate(String(o.event_date).slice(0, 10));
    if (o.expected_ship_date) setExpectedShipDate(String(o.expected_ship_date).slice(0, 10));

    // Auto-select the customer from the PDF (name-stem match against the loaded
    // customer list) so the operator doesn't have to pick it first. Same matcher
    // as edit mode; if there's no confident match it leaves the picker for manual
    // selection. Setting the customer loads the tier SKUs, which the enrichment
    // effect below then uses to validate prices + fill HS/origin on the lines.
    if (o.customer_name && !selectedCustomer && customers.length > 0) {
      const stemOf = (s) => String(s || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim().toLowerCase();
      const want = stemOf(o.customer_name);
      if (want.length >= 3) {
        const mv = customers.find(c => stemOf(c.customer_name) === want)
          || customers.find(c => { const s = stemOf(c.customer_name); return s.length >= 3 && (s.includes(want) || want.includes(s)); });
        if (mv) {
          const stem = String(mv.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
          setSelectedCustomer({
            canonical: stem, tier_name: mv.tier_name, currency: mv.currency,
            variants: customers.filter(c => {
              const s = String(c.customer_name || '').replace(/\s*\([^)]*\)/g, '').split(' - ')[0].trim();
              return s === stem && c.tier_name === mv.tier_name;
            }),
            primary: mv,
          });
          setSelectedVariantQbId(mv.qb_customer_id);
        }
      }
    }

    const items = prefill.line_items || [];
    if (items.length > 0) {
      setLines(items.map((li) => ({
        product_sku: li.product_sku || '',
        quantity_ordered: li.quantity || 1,
        quantity_dispatched: li.quantity || 1,
        unit_price: li.unit_price ? Number(li.unit_price) : '',
        override: !!li.unit_price,        // provisional — the enrich effect re-checks vs tier price
        is_gift: false,                   // a PDF never asks for a give-away; keep all five line
                                          // constructors agreeing on the flag that is now the
                                          // single source of truth for a £0 line
        hs_code: '',
        country_of_origin: '',
        // Carried for the tier-enrichment effect: validate the PDF price against
        // the tier price and fill HS code / origin once the tier SKUs load.
        _pdf_price: li.unit_price ? Number(li.unit_price) : null,
        _needs_tier_enrich: true,
        tier_mismatch: null,
      })));
    }
  };

  // Enrich PDF-applied lines once the tier SKUs are loaded (after a customer is
  // set). Per flagged line: fill HS code + country of origin from the tier master,
  // and validate the PDF price vs the tier price for that SKU + customer:
  //   match  → use the tier price, no "override"
  //   differ → keep the PDF price and flag "Price tier mismatch" (tier vs PDF)
  // Runs once per line (clears _needs_tier_enrich) so it never clobbers manual edits.
  useEffect(() => {
    if (tierSkus.length === 0) return;
    if (!lines.some(l => l._needs_tier_enrich)) return;
    setLines(prev => prev.map(l => {
      if (!l._needs_tier_enrich) return l;
      if (!l.product_sku) return { ...l, _needs_tier_enrich: false };
      const t = tierSkus.find(s => s.sku === l.product_sku);
      if (!t) return { ...l, _needs_tier_enrich: false }; // SKU not in this tier — keep PDF price as override
      const tierPrice = Number(t.unit_price) || 0;
      const pdfPrice = l._pdf_price;
      const mismatch = pdfPrice != null && tierPrice > 0 && Math.abs(pdfPrice - tierPrice) > 0.005;
      return {
        ...l,
        hs_code: l.hs_code || t.hs_code || '',
        country_of_origin: l.country_of_origin || t.country_of_origin || '',
        unit_price: mismatch ? pdfPrice : (tierPrice || (pdfPrice != null ? pdfPrice : '')),
        override: mismatch,
        tier_mismatch: mismatch ? { tier_price: tierPrice, pdf_price: pdfPrice } : null,
        _needs_tier_enrich: false,
      };
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierSkus, lines]);

  // Off-tier line SKUs (e.g. free samples not in this customer's price tier)
  // aren't in tierSkus, so the line picker used to render a blank placeholder
  // even though the SKU is saved. Resolve their names from product_master and
  // expose them as synthetic options so the SKU + name show (Rishabh 2026-07-20).
  const [skuNameMap, setSkuNameMap] = useState({});
  useEffect(() => {
    const known = new Set(tierSkus.map(s => s.sku));
    const missing = [...new Set(lines.map(l => l.product_sku).filter(Boolean))]
      .filter(sku => !known.has(sku) && !(sku in skuNameMap));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const found = {};
      for (const sku of missing) {
        try {
          const r = await api.get(`${WOE_API_BASE}/api/price-tiers/products/search`, { params: { q: sku } });
          const match = (r.data?.data || []).find(p => p.sku === sku);
          found[sku] = match?.product_name || '';
        } catch { found[sku] = ''; }
      }
      if (!cancelled && Object.keys(found).length) setSkuNameMap(prev => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
  }, [tierSkus, lines, skuNameMap]);

  // Live product-search results so off-tier products (e.g. free samples not in
  // the customer's tier) can be ADDED to a line, not just displayed. Searches
  // product_master by SKU or name (debounced) via the price-tiers product search.
  const [productSearchResults, setProductSearchResults] = useState([]);
  const skuSearchTimer = useRef(null);
  const handleSkuSearch = (q) => {
    const term = String(q || '').trim();
    if (skuSearchTimer.current) clearTimeout(skuSearchTimer.current);
    if (term.length < 2) { setProductSearchResults([]); return; }
    skuSearchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`${WOE_API_BASE}/api/price-tiers/products/search`, { params: { q: term, limit: 20 } });
        setProductSearchResults(r.data?.data || []);
      } catch { setProductSearchResults([]); }
    }, 250);
  };

  // tierSkus + synthetic options for (a) any line SKU not in the tier and
  // (b) live product-search matches, so off-tier SKUs render AND can be added.
  const skuOptions = useMemo(() => {
    const currency = tierSkus[0]?.currency || '';
    const seen = new Set(tierSkus.map(s => s.sku));
    const extras = [];
    const pushExtra = (sku, name, price) => {
      if (sku && !seen.has(sku)) {
        seen.add(sku);
        extras.push({
          sku,
          product_name: name || skuNameMap[sku] || '',
          price_source: 'off_tier',
          currency,
          unit_price: Number(price) || 0,
          available_quantity: null,
        });
      }
    };
    for (const l of lines) pushExtra(l.product_sku, l.product_name, l.unit_price);
    for (const p of productSearchResults) pushExtra(p.sku, p.product_name, 0);
    return extras.length ? [...tierSkus, ...extras] : tierSkus;
  }, [tierSkus, lines, skuNameMap, productSearchResults]);

  const addLine = () => setLines([...lines, {
    product_sku: '', quantity_ordered: 1, quantity_dispatched: 1,
    unit_price: '', override: false, is_gift: false, hs_code: '', country_of_origin: ''
  }]);
  const removeLine = (idx) => setLines(lines.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines(lines.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const onPickSku = (idx, sku) => {
    const tierPrice = tierSkus.find(s => s.sku === sku);
    // Two kinds of 0 arrive here and they mean OPPOSITE things:
    //   price_source 'tier'     → an explicit rule someone wrote. Even a 0 is a
    //                             DECISION, so show it verbatim and let the tier
    //                             own the line (this is what main did).
    //   price_source 'unpriced' → a FABRICATED 0. listTierSkus falls back to the
    //                             whole brand catalogue only when the tier has no
    //                             rules at all (service.js:866 short-circuits), and
    //                             those rows are built as `unit_price: shopifyP || 0`
    //                             (service.js:999), so the 0 means "we don't know",
    //                             not "free". Leave it BLANK so the gap is visible
    //                             and the backend's missingPrices guard rejects it.
    // Collapsing both to '' would hide a real £0 tier rule behind an empty box that
    // operators read as "this will be rejected", while it silently books at £0.
    const isTierRule = tierPrice?.price_source === 'tier';
    const knownPrice = isTierRule
      ? Number(tierPrice.unit_price)
      : (tierPrice && Number(tierPrice.unit_price) > 0 ? Number(tierPrice.unit_price) : '');
    updateLine(idx, {
      product_sku: sku || '',
      unit_price: knownPrice,
      // Tier price = canonical, Shopify fallback = override (so it persists as
      // a per-line override and a future tier rule edit won't silently re-price
      // already-placed orders). An unpriced SKU has nothing to override with, so
      // it stays blank/non-override and falls through to the backend guard.
      override: knownPrice === '' ? false : (isTierRule ? false : true),
      is_gift: false,
      hs_code: tierPrice?.hs_code || '',
      country_of_origin: tierPrice?.country_of_origin || ''
    });
  };

  // CHANGE 3 — add a SKU that has NO tier rule (Shopify-fallback / unpriced) to
  // the customer's price tier straight from the order line, via the existing
  // POST /api/price-tiers/:id/items endpoint. Saves the line's typed price when
  // present, else the SKU's displayed (fallback) price. Refreshes tierSkus so
  // the line immediately re-renders as tier-priced.
  const handleAddToTier = async (idx) => {
    const l = lines[idx];
    const sku = l?.product_sku;
    const tierId = activeCustomer?.price_tier_id;
    if (!sku) return;
    if (!tierId) { setError('This customer has no price tier, so a SKU cannot be added to one.'); return; }
    const meta = tierSkus.find(s => s.sku === sku);
    const typed = Number(l.unit_price) > 0 ? Number(l.unit_price) : null;
    const fallback = meta && Number(meta.unit_price) > 0 ? Number(meta.unit_price) : null;
    const priceToSave = typed != null ? typed : fallback;
    // Refuse to write a 0 tier rule. Price tiers are COMPANY-WIDE, so a £0 rule
    // would auto-price this SKU at zero for every customer on the tier. Make the
    // operator type a real price first (a gift is a per-LINE 0, not a tier rule).
    if (priceToSave == null || !(priceToSave > 0)) {
      setError(`Type a unit price for ${sku} on this line first, then add it to the tier (a tier rule can't be 0).`);
      return;
    }
    try {
      setAddingTierSku(sku);
      setError(null);
      await upsertPriceTierItems(tierId, [{ sku, unit_price: priceToSave, notes: 'Added from wholesale order entry' }]);
      // Refresh so this SKU now resolves with price_source: 'tier'.
      const r = await listTierSkus(tierId, brandId || null, warehouseCode || null, expectedShipDate || null);
      // MERGE, never replace. listTierSkus is all-or-nothing: it returns the
      // Shopify-fallback catalogue ONLY while the tier has zero rules
      // (service.js:866 `if (!brandId || tierRows.length > 0) return tierRows`).
      // This button only exists for fallback/unpriced rows, i.e. only in that
      // zero-rule state — so the FIRST successful click writes rule #1 and the
      // refetch comes back holding just that one SKU. Replacing the list would
      // collapse the whole catalogue: every other line's picker would render
      // blank (its `value` lookup misses), thumbnails/stock/cost would vanish
      // from the totals, and the button would disappear from the remaining
      // unpriced lines. Keep the fallback rows the refetch dropped.
      const next = r.data || [];
      const fresh = new Set(next.map(s => s.sku));
      setTierSkus(prev => [...next, ...prev.filter(s => !fresh.has(s.sku))]);
      // Re-resolve THIS line so it genuinely becomes tier-priced (override
      // cleared) rather than keeping the override flag that got it here —
      // otherwise it still persists price_source 'override' and keeps showing the
      // amber caption, contradicting the button's promise.
      // Read from `next` (the response we just received), NOT via onPickSku: that
      // helper resolves against the `tierSkus` in this closure, which setTierSkus
      // has not updated yet, so it would re-find the stale fallback row and set
      // override: true — the very flag we're clearing.
      const tierRow = next.find(s => s.sku === sku);
      if (tierRow?.price_source === 'tier') {
        updateLine(idx, { unit_price: Number(tierRow.unit_price), override: false, is_gift: false });
      }
      setNotice(`Added ${sku} to ${activeCustomer?.tier_name || 'the price tier'}${priceToSave > 0 ? ` at ${priceToSave.toFixed(2)}` : ''}.`);
    } catch (e) {
      setError(`Failed to add ${sku} to tier: ${e.response?.data?.error || e.message}`);
    } finally {
      setAddingTierSku(null);
    }
  };

  const totals = useMemo(() => {
    let qtyOrdered = 0, qtyDispatched = 0, gross = 0, grossOrdered = 0;
    // Cost rollup — per-line unit_cost looked up from tierSkus (product_cogs
    // at the chosen warehouse). totalCost informs the margin display under
    // Grand Total. Excludes lines without a known cost (rendered as '—').
    let totalCost = 0;
    let costKnownLines = 0;
    let costedGross = 0; // dispatched revenue of the cost-known lines only (DT-1617)
    let costCcyMismatchLines = 0;
    const costCcyMismatchPairs = new Set();
    lines.forEach(l => {
      const qo = Number(l.quantity_ordered) || 0;
      const qd = Number(l.quantity_dispatched) || 0;
      const p = Number(l.unit_price) || 0;
      qtyOrdered += qo;
      qtyDispatched += qd;
      grossOrdered += qo * p;
      gross += qd * p; // invoice value uses dispatched
      const item = tierSkus.find(s => s.sku === l.product_sku);
      // Cost is stored in USD but the tier PRICE (unit_price / `net`) is in the order
      // currency. Use `unit_cost_order_ccy` (backend-converted to the order currency)
      // so the margin subtracts like-for-like — never GBP price minus USD cost.
      const c = item?.unit_cost_order_ccy != null ? Number(item.unit_cost_order_ccy)
              : (item?.unit_cost != null ? Number(item.unit_cost) : null);
      const mismatch = c != null ? costCurrencyMismatch(item, selectedCustomer?.currency) : null;
      if (mismatch) {
        // DT-1453: cost could not be converted; leave the line out of the margin.
        costCcyMismatchLines += 1;
        costCcyMismatchPairs.add(`${mismatch.costCcy} to ${mismatch.priceCcy}`);
      } else if (c != null && c >= 0) { totalCost += qd * c; costedGross += qd * p; costKnownLines += 1; }
    });
    const qty = qtyDispatched; // backwards compat with downstream code
    const discount = Number(discountAmount) || 0;
    const freight = Number(freightAmount) || 0;
    const surcharge = Number(surchargeAmount) || 0;
    const taxPctRaw = taxMode === 'exempt' ? 0 : (Number(taxPercent) || 0);
    let net, taxAmount, grandTotal;
    if (taxMode === 'inclusive' && taxPctRaw > 0) {
      // Line prices include tax. Back out the tax for display + invoicing.
      const grossInclusive = gross - discount; // tax-inclusive subtotal
      const netExclusive = grossInclusive / (1 + taxPctRaw / 100);
      taxAmount = grossInclusive - netExclusive;
      net = netExclusive;
      grandTotal = grossInclusive + freight + surcharge; // freight/surcharge typically added on top
    } else {
      // Exclusive (or exempt): tax added on top of net
      net = gross - discount;
      taxAmount = net * (taxPctRaw / 100);
      grandTotal = net + taxAmount + freight + surcharge;
    }
    // Margin computed against `net` (subtotal excluding tax + freight) — the
    // commercial-margin yardstick wholesalers use — restricted to the cost-known
    // lines, so an uncosted line's revenue never counts against no cost (DT-1617).
    // Null when no line has a known cost so the UI hides the margin block entirely.
    const { marginAmount, marginPct } = estimateMargin({ gross, net, costedGross, totalCost, costKnownLines });
    return { qty, qtyOrdered, qtyDispatched, gross, grossOrdered, discount, freight, surcharge, net, taxPct: taxPctRaw, taxAmount, grandTotal, totalCost, costKnownLines, costCcyMismatchLines, costCcyMismatchPairs: Array.from(costCcyMismatchPairs), marginAmount, marginPct };
  }, [lines, discountAmount, taxPercent, taxMode, freightAmount, surchargeAmount, tierSkus, selectedCustomer]);

  const canSubmit =
    !!selectedCustomer && !!brandId && !!warehouseCode && !!eventDate &&
    lines.length > 0 && lines.every(l => l.product_sku && Number(l.quantity_ordered) > 0);

  const submit = async () => {
    if (!canSubmit) {
      setError('Please complete customer, brand, warehouse, date, and at least one line item.');
      return false;
    }
    // A typed £0 with the Free (FOC) tick NOT set is genuinely ambiguous — it means
    // either "give this away" or "I meant to type a real price". Resolving it either
    // way behind the operator's back is the same sin this screen is being cured of:
    // silently gifting it loses stock, and silently tier-pricing it (which is what
    // the payload builder would do, since a 0 fails the `> 0` override test) invoices
    // a customer for something the operator meant as free, and discards their
    // keystroke with only a small caption to notice. So make them resolve it.
    const ambiguousZero = lines
      .map((l, i) => ({ l, i }))
      .filter(({ l }) => !l.is_gift && l.override && l.unit_price === 0 && l.product_sku);
    if (ambiguousZero.length > 0) {
      const which = ambiguousZero.map(({ l, i }) => `line ${i + 1} (${l.product_sku})`).join(', ');
      setError(
        `£0 typed without marking it free on ${which}. ` +
        `Tick "Free (FOC)" to give it away at £0, or clear the price box to use the customer's tier price.`
      );
      return false;
    }
    try {
      setSaving(true); setError(null);
      const products_info = lines.map(l => {
        const qOrd = Number(l.quantity_ordered) || 0;
        const qDisp = Number(l.quantity_dispatched) || 0;
        const item = {
          product_sku: l.product_sku,
          quantity: qDisp, // reservation + revenue calc use dispatched
          quantity_ordered: qOrd,
          quantity_dispatched: qDisp,
          hs_code: l.hs_code || undefined,
          country_of_origin: l.country_of_origin || undefined,
        };
        // Per-line price signalling — FOUR distinct states the backend must tell apart:
        //   FREE    → the Free (FOC) tick, and ONLY that tick → unit_price 0 + is_gift:true
        //             so the backend keeps the 0 and does NOT re-price.
        //   CUSTOM  → operator typed a positive price → send unit_price so it sticks.
        //   BLANK   → left empty ('') → omit unit_price so the backend fills in the tier
        //             price (unchanged legacy behaviour).
        //   UNKNOWN → SKU has no tier rule and no fallback price → also omit unit_price,
        //             so the backend's missingPrices guard REJECTS the order.
        //
        // £0 is NEVER inferred from the price field. A 0 in that box means one of two
        // opposite things — "we decided this is free" and "we don't know what it costs"
        // (listTierSkus returns unit_price: 0 for an unpriced SKU) — and the field cannot
        // tell them apart. Inferring gift from `price === 0` silently booked every
        // unpriced SKU as a free give-away and bypassed the missingPrices guard that
        // exists to catch exactly that. So intent comes from the tick alone; an unpriced
        // line falls through to BLANK and is rejected loudly by the backend.
        const typed = l.unit_price !== '' && l.unit_price != null && Number.isFinite(Number(l.unit_price));
        if (l.is_gift === true) {
          item.unit_price = 0;
          item.is_gift = true;
        } else if (l.override && typed && Number(l.unit_price) > 0) {
          item.unit_price = Number(l.unit_price);
        }
        return item;
      });
      // Build flat shipping_address from structured delivery for backend compat.
      // The ADDRESS part is kept separate from the delivery-instructions tail:
      // instructions are persisted on their own field (delivery_instructions), so
      // folding them in here used to turn an empty-address edit into an
      // "Instructions:"-only string that OVERWROTE the stored address — a silent
      // address wipe on edit (fixed 2026-07-20).
      const shipAddressPart = [
        delivery.company,
        [delivery.first_name, delivery.last_name].filter(Boolean).join(' '),
        delivery.address_1, delivery.address_2,
        [delivery.city, delivery.state, delivery.postal_code].filter(Boolean).join(', '),
        delivery.country,
        delivery.phone ? `Phone: ${delivery.phone}` : '',
        delivery.email ? `Email: ${delivery.email}` : '',
      ].filter(Boolean).join('\n');
      const flatShipping = [
        shipAddressPart,
        deliveryInstructions ? `Instructions: ${deliveryInstructions}` : ''
      ].filter(Boolean).join('\n');
      const flatBilling = [
        billing.company, billing.address_1,
        [billing.city, billing.state, billing.postal_code].filter(Boolean).join(', '),
        billing.country
      ].filter(Boolean).join('\n');
      // On edit we hydrate the structured city/state/etc from the order's columns
      // but NOT the street (address_1/company aren't stored structured). So treat
      // a typed Address 1 / Company as the signal the operator is (re)entering a
      // full address; otherwise preserve the order's stored flat blob verbatim so
      // the street isn't lost to a partial rebuild.
      const hasTypedShipAddr = !!(String(delivery.address_1 || '').trim() || String(delivery.company || '').trim());
      const hasTypedBillAddr = !!(String(billing.address_1 || '').trim() || String(billing.company || '').trim());
      // Over-allocation: if any line's dispatched qty exceeds known available
      // (+inbound) stock, the operator is knowingly over-allocating (system stock
      // can lag the 3PL). Tell the backend to create the order anyway, reserving
      // what's available, instead of hard-rejecting. Mirrors the on-screen
      // "over-allocating … (override allowed)" hint logic exactly.
      const allowOverallocation = lines.some(l => {
        if (!l.product_sku) return false;
        const it = tierSkus.find(s => s.sku === l.product_sku);
        if (!it || it.available_quantity == null) return false;
        const avail = Number(it.available_quantity);
        const inbound = Number(it.inbound_quantity || 0);
        return (Number(l.quantity_dispatched) || 0) > avail + inbound;
      });
      const body = {
        qb_customer_id: activeCustomer.qb_customer_id,
        event_date: eventDate,
        warehouse_code: warehouseCode,
        allow_overallocation: allowOverallocation,
        po_number: poNumber || undefined,
        tracking_number: trackingNumber || undefined,
        carrier: carrier || undefined,
        // Preserve the stored ship-to/bill-to unless the operator (re)typed a
        // street address. Prevents an edit — where the fields are only partially
        // hydrated — from overwriting the full stored address with a partial or
        // empty rebuild. Instructions still save via delivery_instructions.
        shipping_address: hasTypedShipAddr ? flatShipping : (origShippingAddr || shippingAddress || undefined),
        billing_address: hasTypedBillAddr ? flatBilling : (origBillingAddr || undefined),
        shipping_city: delivery.city || undefined,
        shipping_state: delivery.state || undefined,
        shipping_country_code: delivery.country || undefined,
        shipping_zip: delivery.postal_code || undefined,
        delivery_contact: [delivery.first_name, delivery.last_name].filter(Boolean).join(' ') || undefined,
        delivery_phone: delivery.phone || undefined,
        delivery_instructions: deliveryInstructions || undefined,
        order_notes: orderNotes || undefined,
        customer_notes: customerNotes || undefined,
        tags: labels.length ? labels.join(', ') : undefined,
        subsidiary_id: Number(subsidiaryId) || undefined,
        expected_ship_date: expectedShipDate || undefined,
        // tax_rate is stored as a decimal fraction (0.2 = 20%) to match the
        // CIN7-import convention. Without this divide-by-100, downstream
        // reports computing tax = revenue × rate would be 100× off.
        tax_rate: (Number(taxPercent) || 0) / 100,
        taxes: totals.taxAmount,
        tax_status: taxMode === 'inclusive' ? 'Incl' : (taxMode === 'exempt' ? 'Exempt' : 'Excl'),
        shipping_price: Number(freightAmount) || 0,
        surcharge: Number(surchargeAmount) || 0,
        surcharge_description: surchargeAmount > 0 ? 'Surcharge' : undefined,
        discount_amount: Number(discountAmount) || 0,
        products_info,
        source_file: sourceFile || undefined
      };
      let r;
      if (isEditMode) {
        r = await updateWholesaleOrder(parseInt(brandId), editOrderId, body);
        setSuccess(`Updated ${(r.order_id || editOrderId).replace(/^CIN7-/, '')} — ${r.totals.currency} ${Number(r.totals.order_revenue).toFixed(2)}. Inventory holds refreshed.`);
        // Stay on the edit page so user can verify; no field reset.
      } else {
        r = await createWholesaleOrder(parseInt(brandId), body);
        setSuccess(`Created ${r.order_id} — ${r.totals.currency} ${Number(r.totals.order_revenue).toFixed(2)}. Inventory reserved. Order is now in Pending Ship — it will move to Ready-to-Invoice after you ship it.`);
        if (draftId) {
          // Finalized from a draft → discard the draft so it drops off the list,
          // then return to Orders where the new real order now appears.
          try { await deleteOrderDraft(parseInt(brandId), draftId); } catch (_) { /* draft cleanup best-effort */ }
          setDraftId(null);
          setTimeout(() => navigate(listBackTo), 900);
          return;
        }
        // Adopt the just-created order in EDIT mode so the Fulfilment section
        // (dispatch / shipped dates) is available immediately — previously the
        // form reset to blank and the operator had to reopen the order before
        // they could dispatch it (Rishabh 2026-07-16). Inventory was already
        // reserved by the create above. Full navigation so the page remounts
        // cleanly in edit mode and loads the new order.
        if (r.order_id) {
          setTimeout(() => {
            window.location.href = `/wholesale/new?edit=${encodeURIComponent(r.order_id)}&brand=${parseInt(brandId)}`;
          }, 900);
          return;
        }
        // Fallback (create returned no order_id — shouldn't happen): reset to create another.
        setLines([{ product_sku: '', quantity_ordered: 1, quantity_dispatched: 1, unit_price: '', override: false, is_gift: false, hs_code: '', country_of_origin: '' }]);
        setPoNumber(''); setShippingAddress(''); setOrderNotes(''); setDiscountAmount(0);
        setSourceFile(null);
      }
      return true;
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      return false;
    } finally { setSaving(false); }
  };

  // Save the current form (however complete) as a draft. Drafts live in their
  // own table and never reserve stock or hit any total. Upserts when draftId is
  // already set, so repeated saves update the same draft.
  const handleSaveDraft = async () => {
    if (!brandId) { setError('Pick a brand before saving a draft.'); return; }
    try {
      setSavingDraft(true); setError(null);
      const formState = {
        selectedCustomerQbId: activeCustomer?.qb_customer_id || null,
        selectedVariantQbId,
        brandId, warehouseCode, eventDate, expectedShipDate, poNumber,
        trackingNumber, carrier, orderNotes, customerNotes, deliveryInstructions,
        taxPercent, taxMode, freightAmount, surchargeAmount, discountAmount, subsidiaryId,
        lines,
        sourceFile,
      };
      // create-shape line preview for the list row only — never aggregated.
      const products_info = lines.filter(l => l.product_sku).map(l => ({
        product_sku: l.product_sku,
        quantity_ordered: Number(l.quantity_ordered) || 0,
        quantity_dispatched: Number(l.quantity_dispatched) || 0,
        unit_price: Number(l.unit_price) || 0,
      }));
      const res = await saveOrderDraft(parseInt(brandId), {
        draft_id: draftId || undefined,
        order_type: 'wholesale',
        order_name: poNumber || '',
        customer_name: activeCustomer?.customer_name || activeCustomer?.display_name || '',
        customer_id: activeCustomer?.qb_customer_id || '',
        currency: selectedCustomer?.currency || '',
        event_date: eventDate || '',
        products_info,
        draft_payload: formState,
        origin: sourceFile ? 'pdf' : 'manual',
      });
      if (res?.draft_id && !draftId) setDraftId(res.draft_id);
      setSuccess('Draft saved — it appears in your Orders list marked DRAFT and is excluded from all totals.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSavingDraft(false); }
  };

  // Mark this order invoiced from the order page itself — same endpoint the
  // Ready-to-Invoice tab uses. Lets an operator close out the invoice step
  // without leaving the order screen (part of the single-page consolidation).
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceNumberInput, setInvoiceNumberInput] = useState('');
  const [invoiceDateInput, setInvoiceDateInput] = useState('');
  const [invoicing, setInvoicing] = useState(false);
  // dateArg lets the inline "Invoice date" field pass its value directly (React
  // state updates async, so reading invoiceDateInput right after setState would
  // be stale). The dialog path calls with no arg → falls back to state.
  const handleMarkInvoiced = async (dateArg) => {
    if (!invoiceNumberInput.trim()) { setError('Enter an invoice number'); return; }
    try {
      setInvoicing(true); setError(null);
      await markWholesaleOrderInvoiced(parseInt(editBrandId), editOrderId, invoiceNumberInput.trim(), (dateArg || invoiceDateInput) || undefined);
      setInvoiceOpen(false);
      setSuccess(`Invoiced as ${invoiceNumberInput.trim()} — order moved to Invoiced.`);
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setInvoicing(false); }
  };

  // Remove the SCM-side invoice mark and reopen the order for editing. Mirrors
  // the PO Reopen pattern: clears the lock flag + writes an audit row. The real
  // QuickBooks invoice (if one was raised there) must be voided in QB manually.
  const handleVoidInvoice = async () => {
    try {
      setUnInvoicing(true); setError(null);
      await voidWholesaleOrderInvoice(parseInt(editBrandId), editOrderId);
      setUnInvoiceOpen(false);
      setSuccess('Invoice removed — order reopened.');
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setUnInvoicing(false); }
  };

  // Delivered date + tracking/carrier for the Fulfilment box. (The old per-order
  // ship pop-up and its Pending-Ship-era handlers were removed 2026-09-24 — the
  // Fulfilment box's Save is the only ship path on this page.)
  const [shippedDateInput, setShippedDateInput] = useState('');
  // Tracking/carrier live in the order's own fields (manual) or annotations (portal);
  // the Delivered /ship call sends none, so /ship leaves the stored values untouched.
  const shipTracking = '';
  const shipCarrier = '';
  // "Close order — rest won't ship" marker (replaces Pending Ship's "Done").
  const [closedRestWontShip, setClosedRestWontShip] = useState(false);
  const [closingOrder, setClosingOrder] = useState(false);
  const setCloseRestWontShip = async (closed) => {
    try {
      setClosingOrder(true); setError(null);
      await api.post(`${WOE_API_BASE}/api/cogs/brands/${parseInt(editBrandId)}/retail-orders/${encodeURIComponent(editOrderId)}/pending-ship-dismiss`, { dismissed: closed });
      setClosedRestWontShip(closed);
      setSuccess(closed
        ? "Order closed: the remaining quantity won't ship. It no longer shows under To dispatch."
        : 'Order reopened: it shows under To dispatch again.');
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setClosingOrder(false); }
  };

  // Single "Save" for the Fulfilment dates. Applies each transition from the
  // current status + the (editable) date fields. Forward: set a date to advance.
  // Reverse: clear the Dispatch date to roll back to pending (computed stock
  // re-credits). Clearing a Shipped date uses "Revert latest shipment" (real
  // stock movement). Dates are pre-filled from the saved order, so an unchanged
  // Save is a safe no-op.
  const saveFulfilment = async () => {
    const st = String(fulfillmentStatus || '').toLowerCase();
    const dateRe = /^\d{4}-\d{2}-\d{2}$/;
    const o = origFulfil;
    const curDispatch = dispatchDate || '';
    const curShipped = shippedDateInput || '';
    const curInvNum = (invoiceNumberInput || '').trim();
    const curInvDate = invoiceDateInput || '';
    setSavingFulfil(true); setError(null);
    try {
      // Clearing the invoice reference is a deliberate action, not a blank field.
      if (o.invNum && !curInvNum) {
        setError('To remove the invoice reference, use "Remove invoice reference" on the banner.');
        setSavingFulfil(false); return;
      }

      // ── SHIP forward = materialized fulfilment (deducts stock) → /ship.
      //    SHIP clear = stock comes back → must use "Revert latest shipment".
      const shipForward = !o.shipped && curShipped && st !== 'shipped'
        && ['pending', 'processing', 'in_transit', 'partially_shipped'].includes(st);
      const shipClear = !!o.shipped && !curShipped;
      if (shipClear) {
        setError('To undo a shipment, use "Revert latest shipment" — that puts the stock back.');
        setSavingFulfil(false); return;
      }
      // Dispatch is REQUIRED before Delivered (Rishabh 2026-09-24): stock leaves on
      // the Dispatch date; Delivered is optional tracking and can't skip Dispatch.
      if (shipForward && !curDispatch) {
        setError('Set the Dispatch date first. Stock leaves on the Dispatch date; the Delivered date can only be added after that.');
        setSavingFulfil(false); return;
      }
      if (shipForward && !dateRe.test(curShipped)) { setError('Enter a valid Delivered date'); setSavingFulfil(false); return; }

      // ── Everything else is a safe column update (no stock movement):
      //    dispatch set/correct/clear (sync_dispatch_status flips pending↔in_transit,
      //    computed stock follows), ship-date correction, and invoice mark/correct/clear.
      const body = {};
      if (curDispatch !== o.dispatch) {
        if (curDispatch && !dateRe.test(curDispatch)) { setError('Enter a valid Dispatch date'); setSavingFulfil(false); return; }
        body.dispatch_date = curDispatch;
        body.sync_dispatch_status = true;
      }
      if (!shipForward && o.shipped && curShipped && curShipped !== o.shipped) {
        if (!dateRe.test(curShipped)) { setError('Enter a valid Shipped date'); setSavingFulfil(false); return; }
        body.shipped_date = curShipped;
      }
      // Dispatch first (it carries the stock check + deduction), THEN Delivered.
      if (Object.keys(body).length > 0) {
        await updateFulfilmentDates(parseInt(editBrandId), editOrderId, body);
      }
      // A dispatch date set in THIS Save moves the order to in-transit, so the
      // invoice entered alongside it can be saved in the same Save (Rishabh 2026-10-05).
      const dispatchedNow = !!body.dispatch_date;
      const dispatchCleared = 'dispatch_date' in body && !body.dispatch_date;
      if (shipForward) {
        await shipWholesaleOrder(parseInt(editBrandId), editOrderId, {
          shipped_date: curShipped, tracking_number: shipTracking, carrier: shipCarrier,
        });
      }

      // Invoice — first mark, or correction of the existing reference (the
      // number is reference-only while SCM doesn't generate QB invoices).
      const invoiceAttempted = !o.invNum && curInvNum && dateRe.test(curInvDate);
      const invoiceIncomplete = !o.invNum && (!!curInvNum !== !!curInvDate);
      const invoiceAllowed = dispatchedNow || shipForward
        || (['shipped', 'partially_shipped', 'in_transit'].includes(st) && !dispatchCleared);
      const invoiceRefChanged = !!o.invNum && !!curInvNum
        && (curInvNum !== o.invNum || (curInvDate && curInvDate !== o.invDate));
      if ((invoiceAttempted && invoiceAllowed) || invoiceRefChanged) {
        await markWholesaleOrderInvoiced(parseInt(editBrandId), editOrderId, curInvNum, curInvDate || undefined);
      }

      // Never silently drop the invoice fields — that read as "saved" and then
      // confused everyone when the order wasn't invoiced (or, pre-fix, WAS
      // invoiced while still pending and dead-locked the order). When the invoice
      // was NOT recorded, say so and stay on the page (no auto-reload hiding it).
      const invoiceSkipMsg = invoiceIncomplete
        ? 'Invoice NOT saved: enter both the Invoice # and the Invoice date, then Save again.'
        : (invoiceAttempted && !invoiceAllowed)
          ? 'Invoice NOT saved: the order has no Dispatch date. Enter the Dispatch date and Save again (or use Mark Invoiced in Ready to Invoice to invoice an unshipped order).'
          : null;
      if (invoiceSkipMsg) {
        const datesSaved = Object.keys(body).length > 0 || shipForward;
        setError(datesSaved ? `Dates saved. ${invoiceSkipMsg}` : invoiceSkipMsg);
        // Reload only when dates changed (status may have moved); keep the message readable first.
        if (datesSaved) setTimeout(() => window.location.reload(), 6000);
        return;
      }
      setSuccess('Fulfilment saved.');
      setTimeout(() => window.location.reload(), 700);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setSavingFulfil(false); }
  };

  // ONE Save for the whole page (Rishabh 2026-09-18): the single Save button
  // persists the order/lines AND the fulfilment dates/invoice, so there is no
  // separate "Save fulfilment" button.
  //   - Create: just create the order.
  //   - Edit + still editable (pending): update the order+lines first, THEN apply
  //     the fulfilment fields (dispatch / delivered / invoice).
  //   - Edit + dispatched (lines locked → updateWholesaleOrder 409s): skip the
  //     order update and only save the fulfilment fields.
  // submit() (the delete+recreate) only runs on a pending order, so
  // saveFulfilment's baseline (origFulfil — empty on a pending order) stays
  // consistent; no stale-status race.
  const handleSaveAll = async () => {
    if (!isEditMode) { await submit(); return; }
    // Order/lines update only runs on a pending order — updateWholesaleOrder rejects
    // any non-pending status with 409 (mirror that rule exactly, not a status list).
    const isPending = String(fulfillmentStatus || '').toLowerCase() === 'pending';
    if (isPending) {
      const ok = await submit();     // order + lines
      if (!ok) return;               // failed/validation-rejected — error shown, don't proceed
    }
    await saveFulfilment();          // dispatch / delivered / invoice
  };

  if (loading || editLoading || draftLoading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;

  // Connector-imported order (thg_procure et al) — render a dedicated read-only
  // "Connector Order" view. The actual order data (line items, prices, customer
  // link) stays untouched since the upstream portal is source-of-truth. We DO
  // expose narrow internal-annotation fields (notes, tags, warehouse, tracking,
  // carrier) — those are our local fulfillment data and don't need to round-trip
  // to the portal.
  // "Revert latest shipment" dialog — shared by both order views.
  const renderRevertDialog = () => (
    <>
        {/* Revert latest shipment dialog — reason required for audit_log trail. */}
        <Dialog open={revertOpen} onClose={() => !reverting && setRevertOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Revert latest shipment?</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              Inventory deducted by the most recent shipment will be credited back to the originally-deducted batches, and the reservation will be restored. Order status returns to {/* dynamic */}
              <strong>{fulfillmentStatus === 'partially_shipped' ? '"pending" (if no other partials remain) or "partially_shipped"' : '"pending"'}</strong>.
              Audit trail records who/when/why.
            </Typography>
            <TextField
              autoFocus fullWidth multiline minRows={2}
              label="Reason (required for audit)"
              placeholder="e.g. shipped wrong qty, customer cancelled after ship"
              value={revertReason}
              onChange={(e) => setRevertReason(e.target.value)}
              disabled={reverting}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRevertOpen(false)} disabled={reverting}>Cancel</Button>
            <Button
              variant="contained" color="warning"
              disabled={reverting || !revertReason.trim()}
              onClick={async () => {
                try {
                  setReverting(true);
                  setError(null);
                  await unshipWholesaleOrder(parseInt(editBrandId), editOrderId, revertReason.trim());
                  setRevertOpen(false);
                  setRevertReason('');
                  setSuccess('Shipment reverted. Inventory credited back; order is editable again.');
                  // Reload the page so all state (status, reservations, inventory hints) refreshes cleanly.
                  setTimeout(() => window.location.reload(), 600);
                } catch (e) {
                  setError(e.response?.data?.error || e.message);
                } finally { setReverting(false); }
              }}
            >
              {reverting ? 'Reverting…' : 'Confirm revert'}
            </Button>
          </DialogActions>
        </Dialog>
    </>
  );

  // Close / Reopen for a part-shipped order whose remaining qty won't ship
  // (replaces Pending Ship's "Done"; no stock change, just leaves To dispatch).
  const renderCloseOrder = () => {
    if (String(fulfillmentStatus || '').toLowerCase() !== 'partially_shipped' || isVoidedOrder) return null;
    return (
      <Alert severity={closedRestWontShip ? 'success' : 'info'} sx={{ mb: 1.5 }}
        action={(
          <Button color="inherit" size="small" disabled={closingOrder}
            onClick={() => setCloseRestWontShip(!closedRestWontShip)}>
            {closedRestWontShip ? 'Reopen' : "Close order: rest won't ship"}
          </Button>
        )}>
        {closedRestWontShip
          ? "Closed: the remaining quantity won't ship."
          : 'Part-shipped. If the remaining quantity will not ship, close the order.'}
      </Alert>
    );
  };

  // Fulfilment box (Dispatch → Delivered → Invoice). Extracted so the SPS/Procuros page
  // can reuse it once the portal bridges preserve our dispatch columns (2026-09-24).
  const renderFulfilmentBox = (saveHint = 'Dispatch, delivered and invoice save together with the Save button at the bottom of the page.') => {
              const st = String(fulfillmentStatus || '').toLowerCase();
              const canDispatch = ['pending','processing'].includes(st);
              const canShip = ['pending','processing','in_transit','partially_shipped'].includes(st);
              const alreadyInvoiced = !!origFulfil.invNum;
              // Invoice # + date are only persisted once the order is dispatched/shipped
              // (saveFulfilment's invoiceAllowed gate — invoicing a still-pending order is
              // blocked because it used to dead-lock the order). Correcting an EXISTING
              // reference is always allowed. Disable the inputs otherwise so we don't invite
              // input that Save silently drops.
              // A Dispatch date typed on a not-yet-dispatched order unlocks the invoice too:
              // the same Save records the dispatch first, then the invoice (Rishabh 2026-10-05).
              const dispatchPending = canDispatch && !!dispatchDate;
              const invoiceEditable = ['shipped','partially_shipped','in_transit'].includes(st) || alreadyInvoiced || dispatchPending;
              return (
              <Grid item xs={12}>
                <Typography variant="overline" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontSize: 11, letterSpacing: 0.5, lineHeight: 1.5 }}>
                  Fulfilment  ·  {alreadyInvoiced ? 'dispatch & delivery dates stay editable while invoiced' : 'Dispatch (stock leaves) → Delivered → Invoice'}
                </Typography>
                <Grid container spacing={1.5} sx={{ mt: 0.25 }} alignItems="flex-start">
                  {/* Dates are freely editable — nothing happens until Save, so a wrong
                      date can be corrected (or cleared) first. */}
                  <Grid item xs={12} sm={4}>
                    <TextField
                      size="small" type="date" fullWidth label="Dispatch date" required
                      InputLabelProps={{ shrink: true }}
                      value={dispatchDate}
                      disabled={savingFulfil}
                      onChange={(e) => setDispatchDate(e.target.value)}
                      helperText={st === 'in_transit' ? 'Stock left the warehouse on this date. Clear + Save to undo' : canDispatch ? 'Stock leaves the warehouse here (→ In-transit)' : `Status: ${fulfillmentStatus || '—'}`}
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      size="small" type="date" fullWidth label="Delivered date (optional)"
                      InputLabelProps={{ shrink: true }}
                      value={shippedDateInput}
                      disabled={savingFulfil || (!dispatchDate && !origFulfil.shipped)}
                      onChange={(e) => setShippedDateInput(e.target.value)}
                      helperText={st === 'shipped' ? 'Delivered — use "Revert latest shipment" to undo' : !dispatchDate && !origFulfil.shipped ? 'Set the Dispatch date first' : canShip ? 'Optional — date it reached the customer' : `Status: ${fulfillmentStatus || '—'}`}
                      FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <TextField
                        size="small" label="Invoice #" sx={{ width: '45%' }}
                        value={invoiceNumberInput}
                        disabled={savingFulfil || !invoiceEditable}
                        onChange={(e) => setInvoiceNumberInput(e.target.value)}
                      />
                      <TextField
                        size="small" type="date" label="Invoice date" sx={{ flex: 1 }}
                        InputLabelProps={{ shrink: true }}
                        value={invoiceDateInput}
                        disabled={savingFulfil || !invoiceEditable}
                        onChange={(e) => setInvoiceDateInput(e.target.value)}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, mx: 0.5, display: 'block' }}>
                      {alreadyInvoiced
                        ? 'Reference only — edit + Save to correct it (e.g. to match QuickBooks)'
                        : invoiceEditable
                          ? (dispatchPending ? 'Enter # + date, then Save (dispatch is saved first, then the invoice)' : 'Enter # + date, then Save')
                          : 'Set the Dispatch date first — the invoice can be added once the order is dispatched/shipped.'}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  {saveHint}
                </Typography>
              </Grid>
              );
  };

  if (connectorOrder) {
    const o = connectorOrder;
    const lines = o._lines || [];
    const totalOrdered = lines.reduce((s, l) => s + (Number(l.quantity_ordered ?? l.quantity) || 0), 0);
    const totalDispatched = lines.reduce((s, l) => s + (Number(l.quantity_dispatched) || 0), 0);
    const totalReceipted = lines.reduce((s, l) => s + (Number(l.quantity_receipted) || 0), 0);
    const dispatchPct = totalOrdered > 0 ? Math.round((totalDispatched / totalOrdered) * 100) : 0;
    const receiptPct = totalOrdered > 0 ? Math.round((totalReceipted / totalOrdered) * 100) : 0;
    // Translate the connector's lifecycle vocab into a derived "what's happening
    // here" badge. Source-of-truth status stays visible verbatim above; this is
    // for operator scanability.
    const portalStatus = String(o.fulfillment_status || '').toLowerCase();
    let derivedLabel = portalStatus;
    if (portalStatus === 'confirmed') {
      if (totalDispatched === 0) derivedLabel = 'confirmed (not dispatched)';
      else if (totalDispatched < totalOrdered) derivedLabel = `partially dispatched (${dispatchPct}%)`;
      else derivedLabel = 'fully dispatched';
    } else if (portalStatus === 'delivered' && totalReceipted < totalOrdered) {
      derivedLabel = `delivered partial (${receiptPct}%)`;
    }

    // ONE Save for the portal order page (Rishabh 2026-09-24): warehouse/tracking/notes
    // and the line allocations save together (was two separate Save buttons).
    const handleSaveConnectorAll = async () => {
      setConnectorSaving(true); setError(null);
      try {
        await updateConnectorOrderAnnotations(parseInt(editBrandId), editOrderId, connectorAnnotations);
        const payload = lines
          .filter(l => l.line_key)
          .map(l => ({
            line_key: l.line_key,
            allocated_quantity: Number(l.allocated_quantity) || 0,
            allocated_warehouse_code: l.allocated_warehouse_code || '',
            allocated_notes: l.allocated_notes || '',
            local_sku: l.local_sku || '',
          }));
        if (payload.length) await updateConnectorLineAnnotations(parseInt(editBrandId), editOrderId, payload);
      } catch (e) {
        setError(e.response?.data?.error || e.message);
        setConnectorSaving(false);
        return;
      }
      setConnectorSaving(false);
      setSuccess('Saved');
    };

    // Local mutation helper — keeps the `lines` array in sync with operator
    // edits before they save. Since `lines` came from connectorOrder._lines,
    // we update connectorOrder._lines too so the next render picks up the change.
    const updateLineField = (idx, field, value) => {
      const next = lines.map((l, i) => i === idx ? { ...l, [field]: value } : l);
      setConnectorOrder({ ...connectorOrder, _lines: next });
    };

    return (
      <Box>
        <PageHeader
          backTo={true}
          primary={o.customer_name || 'Connector Order'}
          slug={o.order_name || o.order_id}
          metadata={[
            { label: `Source: ${o.source}` },
            { label: 'View-only — upstream system is source of truth. Edits to ordered qty / price would not propagate back.' },
          ]}
          statusBadge={<StatusBadge status={o.fulfillment_status} size="medium" />}
        />

        {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        <Alert severity="info" sx={{ mb: 2 }}>
          Order managed by <strong>{o.source}</strong> connector. Line items, pricing and quantities reflect what the upstream
          portal sent and cannot be edited here — any change would be overwritten on the next sync.
          Internal annotations (warehouse, tracking, notes) are local-only and editable below.
        </Alert>

        {renderCloseOrder()}
        {/* Dispatch / Delivered for portal orders is NOT on this page yet: the SPS and
            Procuros bridges re-insert every order from the portal on each sync and do not
            carry dispatch_date / app-set status, so a dispatch made here would be reverted
            on the next sync (2026-09-24 review). Until the bridges preserve those columns,
            portal orders ship from Pending Ship. */}

        {/* Mark Invoiced is an SCM-side action (records the QB invoice on the order);
            it does NOT touch the upstream portal data, so it's available for connector
            orders too. Reported by Rishabh: the button was missing on portal orders. */}
        {o.qb_invoice_id ? (
          <Alert severity="success" sx={{ mb: 2 }}>Invoiced — QB invoice {o.qb_invoice_id}{o.qb_invoice_date ? ` (${o.qb_invoice_date})` : ''}.</Alert>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Button size="small" variant="contained" onClick={() => { setError(null); setInvoiceOpen(true); }}>Mark Invoiced</Button>
          </Box>
        )}

        {/* Void — legacy Cin7 orders (Cin7 retired, no upstream to defer to). Pending
            only; shipped ones go through the reviewed per-order correction. */}
        {!o.is_voided && ['cin7', 'cin7_wholesale'].includes(o.source) && String(o.fulfillment_status || '').toLowerCase() === 'pending' && (
          <Box sx={{ mb: 2 }}>
            <Button size="small" variant="outlined" color="warning" startIcon={<VoidIcon />}
              onClick={() => { setVoidReason(''); setError(null); setVoidOpen(true); }}>
              Void order
            </Button>
          </Box>
        )}

        {/* Void dialog for the connector-imported view (mirrors the standard form's). */}
        <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Void this order?</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Alert severity="warning" sx={{ mb: 2 }}>
              Voids the order (is_voided=1) and releases any reservation. Reason is required for the audit trail. This cannot be undone via the UI.
            </Alert>
            {o.qb_invoice_id && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This order shows QB invoice {o.qb_invoice_id}. Cin7 is retired, so SCM can't confirm its status — <strong>void that invoice in QuickBooks first</strong>. Confirming here records that you have and clears the stale link.
              </Alert>
            )}
            <TextField fullWidth multiline rows={2} label="Reason *" required autoFocus
              value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Returned — never received by customer, not paid" />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVoidOpen(false)} disabled={voiding}>Cancel</Button>
            <Button variant="contained" color="warning" startIcon={<VoidIcon />}
              disabled={voiding || !voidReason.trim()}
              onClick={async () => {
                try {
                  setVoiding(true);
                  await voidWholesaleOrder(parseInt(editBrandId), editOrderId, voidReason.trim(),
                    { invoice_voided_externally: Boolean(o.qb_invoice_id) });
                  setSuccess(`Voided ${editOrderId.replace(/^CIN7-/, '')} — reason logged.`);
                  setVoidOpen(false);
                  setTimeout(() => navigate(listBackTo), 800);
                } catch (e) {
                  setError(e.response?.data?.error || e.message || 'Failed to void');
                } finally {
                  setVoiding(false);
                }
              }}>
              {voiding ? 'Voiding…' : 'Confirm Void'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Mark Invoiced dialog (shared state with the standard form; this view returns
            before the form's dialog, so it's rendered here too). */}
        <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Mark order invoiced</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Records the QuickBooks invoice on this order and moves it to Invoiced. Does not change the upstream portal data.
            </Typography>
            {!['shipped', 'partially_shipped', 'in_transit'].includes(String(fulfillmentStatus || '').toLowerCase()) && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                This order hasn't been dispatched or shipped yet. Invoicing it now locks editing —
                reversible via "Remove invoice &amp; reopen" on the order banner.
              </Alert>
            )}
            <TextField fullWidth autoFocus label="Invoice number *" sx={{ mb: 2 }}
              value={invoiceNumberInput} onChange={(e) => setInvoiceNumberInput(e.target.value)}
              placeholder="QB invoice #" disabled={invoicing} />
            <TextField fullWidth type="date" label="Invoice date" InputLabelProps={{ shrink: true }}
              value={invoiceDateInput} onChange={(e) => setInvoiceDateInput(e.target.value)}
              helperText="Leave blank to use today" disabled={invoicing} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvoiceOpen(false)} disabled={invoicing}>Cancel</Button>
            <Button variant="contained" onClick={() => handleMarkInvoiced()} disabled={invoicing || !invoiceNumberInput.trim()}>
              {invoicing ? 'Marking…' : 'Mark Invoiced'}
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container spacing={2}>
          {/* Header / summary */}
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Order header</Typography>
                <Grid container spacing={1.5} sx={{ fontSize: 13 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Order ID</Typography>
                    <Typography fontFamily="monospace">{o.order_id}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Order name</Typography>
                    <Typography fontFamily="monospace">{o.order_name || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Currency</Typography>
                    <Typography>{o.currency || '—'}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="textSecondary">Portal status</Typography>
                    <Typography>
                      <StatusBadge status={o.fulfillment_status} size="small" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary">Derived</Typography>
                    <Typography color={dispatchPct < 100 ? 'warning.dark' : 'success.dark'} fontWeight={500}>
                      {derivedLabel}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="textSecondary">Total ordered</Typography>
                    <Typography fontFamily="monospace">{totalOrdered.toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="textSecondary">Total dispatched</Typography>
                    <Typography fontFamily="monospace">{totalDispatched.toLocaleString()} ({dispatchPct}%)</Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Typography variant="caption" color="textSecondary">Total receipted (portal)</Typography>
                    <Typography fontFamily="monospace">{totalReceipted.toLocaleString()} ({receiptPct}%)</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6" fontWeight={600}>Line items</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Portal qtys (Ordered / Dispatched / Receipted) come from {o.source} — read-only.
                      <strong> Allocate</strong> is our internal dispatch plan + warehouse stock.
                      {' '}<strong>When set, the allocated qty overrides the shipped qty for the stock deduction.</strong>
                    </Typography>
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 56 }}></TableCell>
                        <TableCell>Our SKU</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Portal SKU / EAN</TableCell>
                        <TableCell align="right">Ordered</TableCell>
                        <TableCell align="right">Dispatched</TableCell>
                        <TableCell align="right">Receipted</TableCell>
                        <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>Allocate (internal)</TableCell>
                        <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>Available {o.warehouse_code ? `@ ${o.warehouse_code}` : ''}</TableCell>
                        <TableCell align="right">Unit price</TableCell>
                        <TableCell align="right">Tier price</TableCell>
                        <TableCell align="right">Line total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lines.map((l, idx) => {
                        const qOrd = Number(l.quantity_ordered ?? l.quantity) || 0;
                        const qDisp = Number(l.quantity_dispatched) || 0;
                        const qRcpt = Number(l.quantity_receipted) || 0;
                        const price = Number(l.unit_price) || 0;
                        const total = qOrd * price;
                        const partial = qDisp > 0 && qDisp < qOrd;
                        // Backend enriches each line with local_sku / local_product_name /
                        // local_thumbnail_url by joining product_master on primary_gtin
                        // (with product_identifiers as fallback) = ean/gtin/upc.
                        // local_match tells us whether the lookup hit. Falls back to
                        // portal sku + (unmapped) warning when the GTIN isn't in either.
                        const portalSku = l.connector_product_sku || l.product_sku || l.product_erp_id || '—';
                        const ourSku = l.local_sku;
                        const ourName = l.local_product_name;
                        const thumb = l.local_thumbnail_url || l.local_image_url;
                        // Price-tier check (Rishabh 2026-08-05): show this SKU's price
                        // from the customer's price tier (already loaded in tierSkus,
                        // in the order currency) next to the portal's unit price, and
                        // flag when the portal charged something other than the agreed
                        // tier price. null tier price → SKU not in the tier → show "—".
                        const tierRow = ourSku ? tierSkus.find(s => s.sku === ourSku) : null;
                        const tierPrice = tierRow ? (Number(tierRow.unit_price) || 0) : null;
                        const tierMismatch = tierPrice != null && tierPrice > 0 && Math.abs(price - tierPrice) > 0.005;
                        return (
                          <TableRow key={idx} sx={partial ? { bgcolor: 'warning.lighter' } : {}}>
                            <TableCell>
                              {thumb ? (
                                <Box component="img" src={thumb} alt={ourSku || portalSku}
                                  sx={{ width: 40, height: 40, borderRadius: 0.5, objectFit: 'cover', border: '1px solid #ddd' }}
                                  onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                              ) : (
                                <Box sx={{ width: 40, height: 40, borderRadius: 0.5, bgcolor: 'grey.200',
                                           display: 'flex', alignItems: 'center', justifyContent: 'center',
                                           fontSize: 9, color: 'grey.500' }}>
                                  {ourSku ? 'no img' : '—'}
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                              {ourSku || (
                                <Typography variant="caption" color="warning.dark" sx={{ fontStyle: 'italic' }}
                                  title="No product_master / product_identifiers entry matches this EAN — add this product to the catalogue to enable matching">
                                  (unmapped)
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: 12 }}>{ourName || l.product_name || '—'}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11, color: 'text.secondary' }}>
                              <div>{portalSku}</div>
                              {l.ean && <div style={{ opacity: 0.7 }}>EAN {l.ean}</div>}
                            </TableCell>
                            <TableCell align="right">{qOrd.toLocaleString()}</TableCell>
                            <TableCell align="right">{qDisp.toLocaleString()}</TableCell>
                            <TableCell align="right">{qRcpt.toLocaleString()}</TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'action.hover' }}>
                              {ourSku ? (
                                <TextField size="small" type="number"
                                  value={l.allocated_quantity ?? 0}
                                  onChange={(e) => updateLineField(idx, 'allocated_quantity', Math.max(0, Number(e.target.value) || 0))}
                                  inputProps={{ min: 0, step: 1, style: { textAlign: 'right', padding: '4px 8px' } }}
                                  sx={{ width: 90 }} />
                              ) : (
                                <Typography variant="caption" color="textSecondary" fontStyle="italic">map first</Typography>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ bgcolor: 'action.hover', fontFamily: 'monospace', fontSize: 12 }}>
                              {l.available_at_order_warehouse == null ? (
                                <Typography variant="caption" color="textSecondary" fontStyle="italic">
                                  {!ourSku ? '—' : (!o.warehouse_code ? 'set warehouse' : 'no stock')}
                                </Typography>
                              ) : (() => {
                                const avail = Number(l.available_at_order_warehouse) || 0;
                                const alloc = Number(l.allocated_quantity) || 0;
                                const short = alloc > avail;
                                return (
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace', color: short ? 'error.dark' : (avail === 0 ? 'error.dark' : 'success.dark'), fontWeight: short ? 600 : 400 }}
                                    title={short ? `Allocated ${alloc} exceeds available ${avail.toLocaleString()}` : ''}>
                                    {avail.toLocaleString()}
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                            <TableCell align="right">{o.currency} {price.toFixed(2)}</TableCell>
                            <TableCell align="right" sx={tierMismatch ? { bgcolor: 'warning.lighter' } : {}}>
                              {tierPrice == null ? (
                                <Typography variant="caption" color="text.secondary">—</Typography>
                              ) : (
                                <Typography variant="body2"
                                  sx={{ fontFamily: 'monospace', fontSize: 13, color: tierMismatch ? 'warning.dark' : 'text.primary', fontWeight: tierMismatch ? 700 : 400 }}
                                  title={tierMismatch ? `Portal price ${o.currency} ${price.toFixed(2)} ≠ tier price ${o.currency} ${tierPrice.toFixed(2)}` : 'Matches the portal price'}>
                                  {o.currency} {tierPrice.toFixed(2)}{tierMismatch ? ' ⚠' : ''}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">{o.currency} {total.toFixed(2)}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Internal annotations sidebar */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>Internal annotations</Typography>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
                  Local-only fields. Not sent back to {o.source}.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField size="small" fullWidth label="Warehouse code"
                    placeholder="e.g. G3PL — where we fulfill from"
                    value={connectorAnnotations.warehouse_code || ''}
                    onChange={(e) => setConnectorAnnotations({ ...connectorAnnotations, warehouse_code: e.target.value })} />
                  <TextField size="small" fullWidth label="Tracking number"
                    value={connectorAnnotations.tracking_number || ''}
                    onChange={(e) => setConnectorAnnotations({ ...connectorAnnotations, tracking_number: e.target.value })} />
                  <TextField size="small" fullWidth label="Carrier"
                    value={connectorAnnotations.carrier || ''}
                    onChange={(e) => setConnectorAnnotations({ ...connectorAnnotations, carrier: e.target.value })} />
                  <TextField size="small" fullWidth label="Tags" placeholder="comma-separated"
                    value={connectorAnnotations.tags || ''}
                    onChange={(e) => setConnectorAnnotations({ ...connectorAnnotations, tags: e.target.value })} />
                  <TextField size="small" fullWidth multiline minRows={3} label="Internal notes"
                    value={connectorAnnotations.order_notes || ''}
                    onChange={(e) => setConnectorAnnotations({ ...connectorAnnotations, order_notes: e.target.value })} />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button size="small" onClick={() => navigate(-1)} disabled={connectorSaving}>Back</Button>
                    <Button size="small" variant="contained" onClick={handleSaveConnectorAll} disabled={connectorSaving || savingFulfil}>
                      {connectorSaving || savingFulfil ? 'Saving…' : 'Save'}
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  // Once an order is DISPATCHED (dispatch date set → in_transit or beyond), its
  // quantities are locked — you can't change what has already gone out. To edit,
  // clear the Dispatched Date + save (undispatch → pending), then edit.
  // (Rishabh 2026-07-16.)
  const quantitiesLocked = isEditMode &&
    ['in_transit', 'partially_shipped', 'shipped', 'fulfilled', 'delivered'].includes(
      String(fulfillmentStatus || '').toLowerCase()
    );

  return (
    <Box>
      <PageHeader
        backTo={isEditMode ? true : '/wholesale'}
        primary={isEditMode ? (selectedCustomer?.canonical || 'Edit Wholesale Order') : 'New Wholesale Order'}
        slug={isEditMode ? editOrderId.replace(/^CIN7-/, '') : null}
        metadata={[
          {
            label: isEditMode
              ? 'Editable until ship. Saving re-runs reservations.'
              : 'Pick a customer → tier/subsidiary/terms autofill.',
          },
        ]}
      />

      {error && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>{error}</Alert>}
      {viewOnlyReason && (
        <Alert
          severity={['shipped','partially_shipped'].includes(fulfillmentStatus) ? 'info' : 'warning'}
          sx={{ mb: 1.5 }}
          action={(
            <Box sx={{ display: 'flex', gap: 1 }}>
              {/* Ship + Invoice are now driven by date fields above Delivery & Billing
                  (Rishabh 2026-06-30) — only the shipment-undo stays here. Hidden
                  while an invoice reference exists (unship 409s until it's removed). */}
              {!actionsLocked && !origFulfil.invNum && ['shipped','partially_shipped'].includes(fulfillmentStatus) && (
                <Button color="inherit" size="small" onClick={() => { setError(null); setRevertOpen(true); }} disabled={reverting}>
                  Revert latest shipment
                </Button>
              )}
            </Box>
          )}
        >
          {viewOnlyReason}
        </Alert>
      )}

      {renderCloseOrder()}

      {/* Invoice reference — informational, never a lock (SCM doesn't create or
          sync QuickBooks invoices). Correctable in the Fulfilment section. */}
      {origFulfil.invNum && !isVoidedOrder && (
        <Alert
          severity="info"
          sx={{ mb: 1.5 }}
          action={(
            <Button color="inherit" size="small" onClick={() => { setError(null); setUnInvoiceOpen(true); }} disabled={unInvoicing}>
              Remove invoice reference
            </Button>
          )}
        >
          Invoice reference on file: <b>{origFulfil.invNum}</b>{origFulfil.invDate ? ` (${origFulfil.invDate})` : ''} —
          SCM doesn't create or sync QuickBooks invoices, so this is a reference only.
          If it doesn't match QB, correct it in the Fulfilment section below and Save.
        </Alert>
      )}

      {/* Remove-invoice-reference confirm */}
      <Dialog open={unInvoiceOpen} onClose={() => !unInvoicing && setUnInvoiceOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove invoice reference?</DialogTitle>
        <DialogContent>
          {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            This clears the reference <b>{origFulfil.invNum}</b> from this order in SCM (audited)
            and returns it to the Ready-to-Invoice queue. Inventory is not touched.
            To just correct a wrong number, edit it in the Fulfilment section instead.
          </Typography>
          <Alert severity="info" sx={{ mt: 1.5 }}>
            Any real invoice in QuickBooks is unaffected — SCM only removes its own reference.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnInvoiceOpen(false)} disabled={unInvoicing}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleVoidInvoice} disabled={unInvoicing}>
            {unInvoicing ? 'Removing…' : 'Remove reference'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Success alert moved next to the submit button in the sticky bar below
          (see bottom of the form). Saves the user from scrolling up to see the
          confirmation. */}

      <Card sx={{ mb: 1.5 }} variant="outlined">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                options={customerGroups}
                getOptionLabel={(o) => o.canonical || ''}
                value={selectedCustomer}
                onChange={(_, v) => {
                  customerChangedByUserRef.current = true;
                  setSelectedCustomer(v);
                }}
                renderInput={(params) => <TextField {...params} size="small" label="Customer *"
                  placeholder="Type retailer name…" />}
                isOptionEqualToValue={(a, b) => a?.canonical === b?.canonical && a?.tier_name === b?.tier_name}
                filterOptions={(opts, state) => {
                  const q = state.inputValue.trim().toLowerCase();
                  if (!q) return opts;
                  return opts.filter(o =>
                    (o.canonical || '').toLowerCase().includes(q) ||
                    (o.tier_name || '').toLowerCase().includes(q) ||
                    o.variants.some(v =>
                      (v.delivery_city || '').toLowerCase().includes(q) ||
                      (v.delivery_country || '').toLowerCase().includes(q) ||
                      (v.qb_customer_id || '').toLowerCase().includes(q)
                    )
                  );
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={`${option.canonical}__${option.tier_name}`} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography variant="body2" fontWeight="600" sx={{ flex: 1 }}>{option.canonical}</Typography>
                      <Chip size="small"
                        label={option.tier_name || '(no tier)'}
                        color={option.tier_name ? 'primary' : 'default'} />
                    </Box>
                    <Typography variant="caption" color="textSecondary">
                      {option.variants.length > 1
                        ? `${option.variants.length} ship-to addresses · ${option.currency ? `${option.currency} pricing` : 'pricing currency not set'}`
                        : `${[option.primary.delivery_city, option.primary.delivery_country].filter(Boolean).join(', ') || '(no address on file)'}${option.tier_name ? ` · ${option.currency ? `${option.currency} pricing` : 'pricing currency not set'}` : ''}`
                      }
                    </Typography>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Brand *</InputLabel>
                <Select value={brandId} label="Brand *" onChange={(e) => setBrandId(e.target.value)}>
                  {brands.map(b => (
                    <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {selectedCustomer && activeCustomer && (
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, bgcolor: 'action.hover' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, minWidth: 180 }}>
                  {selectedCustomer.canonical}
                </Typography>
                {selectedCustomer.tier_name ? (
                  <Chip size="small" color="primary"
                    label={`${selectedCustomer.tier_name} (${selectedCustomer.currency || 'currency not set'})`} />
                ) : (
                  <Chip size="small" color="warning" label="No Price Tier" />
                )}
                {activeCustomer.contact_first_name && (
                  <Typography variant="caption" color="textSecondary">
                    {activeCustomer.contact_first_name} {activeCustomer.contact_last_name}{activeCustomer.contact_email ? ` · ${activeCustomer.contact_email}` : ''}
                  </Typography>
                )}
                <Button size="small" variant="outlined"
                  component={RouterLink} to={`/retail/customers/${encodeURIComponent(activeCustomer.qb_customer_id)}/history`}
                  target="_blank" rel="noopener noreferrer"
                  title="Opens in a new tab so your in-progress order isn't lost"
                  sx={{ py: 0.25, px: 1, fontSize: 12 }}>
                  History ↗
                </Button>
              </Box>
              {/* Show "shared with N retailers" hint when tier is shared */}
              {selectedCustomer.tier_name && (() => {
                const sharedCount = customers.filter(c => c.tier_name === selectedCustomer.tier_name && c.qb_customer_id !== activeCustomer.qb_customer_id && (c.customer_name || '').split(' - ')[0].trim() !== selectedCustomer.canonical).length;
                if (sharedCount === 0) return null;
                return (
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1, fontSize: 11 }}>
                    Tier shared with {sharedCount} other retailer{sharedCount === 1 ? '' : 's'} — edits to tier prices apply to all. Override per-customer on the line "Unit Price".
                  </Typography>
                );
              })()}
              {!selectedCustomer.tier_name && (
                <Alert severity="warning" sx={{ mb: 1, py: 0.5, fontSize: 12 }}>
                  No price tier assigned — SKU prices won't auto-fill. Edit this customer in <strong>Retail → Customers</strong>.
                </Alert>
              )}
              {/* Ship-to picker: only shown when this retailer has >1 ship-to address */}
              {selectedCustomer.variants.length > 1 && (
                <Box sx={{ mb: 1, p: 1, bgcolor: 'background.paper', borderRadius: 1, border: '1px dashed', borderColor: 'primary.main' }}>
                  <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 0.5, fontSize: 11 }}>
                    {selectedCustomer.variants.length} ship-to addresses on file — pick one:
                  </Typography>
                  <FormControl size="small" fullWidth>
                    <Select value={selectedVariantQbId || ''}
                      onChange={(e) => {
                        customerChangedByUserRef.current = true;
                        setSelectedVariantQbId(e.target.value);
                      }}>
                      {selectedCustomer.variants.map(v => (
                        <MenuItem key={v.qb_customer_id} value={v.qb_customer_id}>
                          {(v.customer_name || '').replace(/^[^-]+-\s*/, '') || '(default)'}
                          {(v.delivery_city || v.delivery_country) ? ` — ${[v.delivery_city, v.delivery_country].filter(Boolean).join(', ')}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Grid container spacing={1} sx={{ '& .MuiTypography-caption': { fontSize: 10, lineHeight: 1.2 }, '& .field-value': { fontSize: 13, lineHeight: 1.4 } }}>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">PAYMENT TERMS</Typography>
                  <Typography className="field-value">{activeCustomer.payment_terms || '—'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">SEND VIA</Typography>
                  <Box><Chip size="small" label={activeCustomer.send_method || 'qb'} sx={{ height: 20, fontSize: 11 }} /></Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">{taxTerm.toUpperCase()} RATE</Typography>
                  <Typography className="field-value">{activeCustomer.tax_rate || '(none)'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">DEFAULT WAREHOUSE</Typography>
                  <Typography className="field-value">{activeCustomer.default_warehouse_code || '(auto by geography)'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="textSecondary">CUSTOMER EMAIL</Typography>
                  <Typography className="field-value" sx={{ fontFamily: 'monospace' }}>{activeCustomer.contact_email || '—'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">CUSTOMER PHONE</Typography>
                  <Typography className="field-value">{activeCustomer.contact_phone || '—'}</Typography>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Typography variant="caption" color="textSecondary">CIN7 ID</Typography>
                  <Typography className="field-value" sx={{ fontFamily: 'monospace' }}>{(activeCustomer.qb_customer_id || '').replace(/^CIN7-/, '')}</Typography>
                </Grid>
              </Grid>
            </Paper>
          )}

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Order Date *" type="date" value={eventDate}
                onChange={(e) => setEventDate(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Expected Ship Date" type="date"
                value={expectedShipDate}
                onChange={(e) => setExpectedShipDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText="When you plan to ship — drives the inbound-inventory check on each line"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Ship From Warehouse *</InputLabel>
                <Select value={warehouseCode} label="Ship From Warehouse *" onChange={(e) => setWarehouseCode(e.target.value)}>
                  {warehouses.map(w => (
                    <MenuItem key={w.id} value={w.code}>{w.code} — {w.name} ({w.country || '?'})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Invoicing Subsidiary *</InputLabel>
                <Select value={subsidiaryId || 0} label="Invoicing Subsidiary *"
                  onChange={(e) => setSubsidiaryId(Number(e.target.value))}>
                  {/* /api/subsidiaries doesn't surface is_active in its payload,
                      so filter on `=== false` (treating absent / undefined as
                      active). Without this guard the dropdown was empty. */}
                  {(subList || []).filter(s => s.is_active !== false).map(s => (
                    <MenuItem key={s.id} value={Number(s.id)}>
                      {s.name}{s.is_default ? ' (parent)' : ''} — {s.country} {s.currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Customer PO #" value={poNumber}
                onChange={(e) => setPoNumber(e.target.value)} placeholder="e.g. PO-12345" />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Tracking #" value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Set at ship; pre-fill if known" />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Carrier" value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                placeholder="UPS / FedEx / DHL" />
            </Grid>
            <Grid item xs={6} md={3}>
              {/* Order labels/flags (Gift Tag / Hoover) — plain metadata, stored in tags. */}
              <FormControl fullWidth size="small">
                <InputLabel>Labels</InputLabel>
                <Select
                  multiple
                  value={labels}
                  label="Labels"
                  onChange={(e) => setLabels(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                >
                  {ORDER_LABEL_OPTIONS.map((opt) => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Discount" type="number" value={discountAmount}
                onChange={(e) => setDiscountAmount(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Freight" type="number" value={freightAmount}
                onChange={(e) => setFreightAmount(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label="Surcharge" type="number" value={surchargeAmount}
                onChange={(e) => setSurchargeAmount(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, step: 0.01 }} />
            </Grid>
            <Grid item xs={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>{taxTerm} Calculation</InputLabel>
                <Select value={taxMode} label={`${taxTerm} Calculation`}
                  onChange={(e) => setTaxMode(e.target.value)}>
                  <MenuItem value="exclusive">Exclusive — {taxTerm.toLowerCase()} added on top</MenuItem>
                  <MenuItem value="inclusive">Inclusive — {taxTerm.toLowerCase()} already in price</MenuItem>
                  <MenuItem value="exempt">Exempt — no {taxTerm.toLowerCase()}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField fullWidth size="small" label={`${taxTerm} %`}
                type="number" value={taxPercent}
                onChange={(e) => setTaxPercent(Math.max(0, Number(e.target.value) || 0))}
                inputProps={{ min: 0, max: 100, step: 0.5 }}
                InputProps={{ endAdornment: <span style={{ color: '#666' }}>%</span> }}
                disabled={taxMode === 'exempt'}
                helperText={taxMode === 'exempt' ? `No ${taxTerm.toLowerCase()} applied` : (taxLabel || `auto from customer`)}
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }}
              />
            </Grid>
            {/* Invoicing is a finance event — it locks the ORDER (lines/prices/customer)
                but NOT shipment logistics. Dispatch/shipped dates + Save stay available
                on an invoiced order (Rishabh, WHSM59-1: ops corrects dates after the
                invoice is raised — standard ERP behaviour). Voided orders stay locked. */}
            {isEditMode && !isVoidedOrder && renderFulfilmentBox()}
            <Grid item xs={12}>
              <Typography variant="overline" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontSize: 11, letterSpacing: 0.5, lineHeight: 1.5 }}>
                Delivery & Billing  ·  auto-filled from customer master
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                  <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Delivery Address</Typography>
                  <Button size="small" sx={{ fontSize: 11, py: 0.25 }} onClick={() => setBilling({
                    company: delivery.company, address_1: delivery.address_1,
                    city: delivery.city, state: delivery.state, postal_code: delivery.postal_code, country: delivery.country
                  })}>Copy → Billing</Button>
                </Box>
                {customerAddresses.length > 0 && (
                  <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                    <InputLabel>Ship to</InputLabel>
                    <Select value={shipToId} label="Ship to" onChange={(e) => applyShipTo(e.target.value)}>
                      <MenuItem value={0}>Primary (customer master)</MenuItem>
                      {customerAddresses.map(a => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.label || [a.address_1, a.city].filter(Boolean).join(', ') || `Address ${a.id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <Grid container spacing={0.75}>
                  <Grid item xs={6}><TextField size="small" fullWidth label="First Name" value={delivery.first_name} onChange={(e) => setDelivery({ ...delivery, first_name: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Last Name" value={delivery.last_name} onChange={(e) => setDelivery({ ...delivery, last_name: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Company" value={delivery.company} onChange={(e) => setDelivery({ ...delivery, company: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Address 1" value={delivery.address_1} onChange={(e) => setDelivery({ ...delivery, address_1: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Address 2" value={delivery.address_2} onChange={(e) => setDelivery({ ...delivery, address_2: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="City" value={delivery.city} onChange={(e) => setDelivery({ ...delivery, city: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="State / Region" value={delivery.state} onChange={(e) => setDelivery({ ...delivery, state: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Postal Code" value={delivery.postal_code} onChange={(e) => setDelivery({ ...delivery, postal_code: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Country" value={delivery.country} onChange={(e) => setDelivery({ ...delivery, country: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Email" value={delivery.email} onChange={(e) => setDelivery({ ...delivery, email: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Phone" value={delivery.phone} onChange={(e) => setDelivery({ ...delivery, phone: e.target.value })} /></Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 1.25 }}>
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>Billing Address</Typography>
                <Grid container spacing={0.75}>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Company" value={billing.company} onChange={(e) => setBilling({ ...billing, company: e.target.value })} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Address 1" value={billing.address_1} onChange={(e) => setBilling({ ...billing, address_1: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="City" value={billing.city} onChange={(e) => setBilling({ ...billing, city: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="State / Region" value={billing.state} onChange={(e) => setBilling({ ...billing, state: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Postal Code" value={billing.postal_code} onChange={(e) => setBilling({ ...billing, postal_code: e.target.value })} /></Grid>
                  <Grid item xs={6}><TextField size="small" fullWidth label="Country" value={billing.country} onChange={(e) => setBilling({ ...billing, country: e.target.value })} /></Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Internal Comments (not on invoice)" value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)} multiline rows={2}
                helperText="Visible only to your team"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Customer Notes (on invoice)" value={customerNotes}
                onChange={(e) => setCustomerNotes(e.target.value)} multiline rows={2}
                helperText="Shown on the invoice"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Delivery Instructions" value={deliveryInstructions}
                onChange={(e) => setDeliveryInstructions(e.target.value)} multiline rows={2}
                helperText="e.g. 'Loading dock 3, 9–11am only'"
                FormHelperTextProps={{ sx: { fontSize: 10, mx: 0.5 } }} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 1.5 }} variant="outlined">
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          {quantitiesLocked && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              This order has been dispatched, so the line items (SKUs, quantities, prices) are locked. To change them, clear the Dispatch date (undispatch the order) in the Fulfilment section, save, and then edit.
            </Alert>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle1" fontWeight={700}>Line Items</Typography>
            <Box display="flex" gap={1} alignItems="center">
              {sourceFile && isEditMode && (
                <Button size="small" variant="text" onClick={() => downloadOrderSourcePdf(parseInt(brandId), editOrderId)}>
                  View Source PDF
                </Button>
              )}
              {sourceFile && !isEditMode && (
                <Tooltip title="The uploaded PDF is stored and will be linked to this order when you save">
                  <Chip size="small" color="success" variant="outlined" label="PDF attached" />
                </Tooltip>
              )}
              <Button size="small" variant="outlined" onClick={() => setPdfOpen(true)}>Upload PDF</Button>
              <Button size="small" startIcon={<AddIcon fontSize="small" />} onClick={addLine} disabled={quantitiesLocked}>Add Line</Button>
            </Box>
          </Box>
          <OrderPdfUpload
            open={pdfOpen}
            onClose={() => setPdfOpen(false)}
            onApply={applyPdfPrefill}
            orderType="wholesale"
            brandId={brandId}
            tierId={selectedCustomer?.price_tier_id}
            warehouseCode={warehouseCode}
          />
          <TableContainer>
            <Table size="small" sx={{ '& .MuiTableCell-head': { fontSize: 11, fontWeight: 700, py: 0.75, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.3 }, '& .MuiTableCell-body': { py: 0.75 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>SKU *</TableCell>
                  <TableCell align="right">Qty Ord *</TableCell>
                  <TableCell align="right">Qty Disp</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell>HS Code</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                  <TableCell align="right">Margin</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lines.map((l, idx) => {
                  const qDisp = Number(l.quantity_dispatched) || 0;
                  const qOrd = Number(l.quantity_ordered) || 0;
                  const total = qDisp * (Number(l.unit_price) || 0);
                  const isShort = qDisp < qOrd && qOrd > 0;
                  return (
                    <TableRow key={idx} sx={{
                      // Top-align every cell in the line-item row. Without this,
                      // optional hints under Qty Disp ("Available at WH: N",
                      // "short by N"), Unit Price ("override", fallback note),
                      // and the SKU row's product-name caption push their host
                      // cell taller while sibling cells stay centered — Qty Disp
                      // input visibly drifts up while Unit Price/HS Code/Origin
                      // stay middle-aligned. Top-aligning makes inputs share a
                      // baseline; hints flow below in their own cells.
                      '& td': { verticalAlign: 'top' },
                      ...(isShort ? { bgcolor: 'warning.lighter' } : {}),
                    }}>
                      <TableCell sx={{ minWidth: 360 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {/* Thumbnail of selected SKU */}
                          {(() => {
                            const item = tierSkus.find(s => s.sku === l.product_sku);
                            const img = item?.thumbnail_url || item?.image_url;
                            return img ? (
                              <Box component="img" src={img} alt={l.product_sku}
                                sx={{ width: 40, height: 40, borderRadius: 1, objectFit: 'cover', border: '1px solid #ddd' }}
                                onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <Box sx={{ width: 40, height: 40, borderRadius: 1, bgcolor: 'grey.200',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'grey.500' }}>
                                {l.product_sku ? 'no img' : '—'}
                              </Box>
                            );
                          })()}
                          <Box sx={{ flex: 1 }}>
                            <Autocomplete
                              size="small" options={skuOptions} value={skuOptions.find(s => s.sku === l.product_sku) || null}
                              onChange={(_, v) => onPickSku(idx, v?.sku)}
                              onInputChange={(_, val, reason) => { if (reason === 'input') handleSkuSearch(val); }}
                              filterOptions={(opts, state) => {
                                const q = state.inputValue.trim().toLowerCase();
                                if (!q) return opts;
                                return opts.filter(o =>
                                  (o.sku || '').toLowerCase().includes(q) ||
                                  (o.product_name || '').toLowerCase().includes(q));
                              }}
                              getOptionLabel={(o) => o?.sku || ''}
                              isOptionEqualToValue={(a, b) => a?.sku === b?.sku}
                              groupBy={(o) => o.price_source || 'tier'}
                              renderGroup={(params) => {
                                const label = params.group === 'tier'
                                  ? '✓ Tier-priced'
                                  : params.group === 'shopify_fallback'
                                    ? '⚠ Shopify fallback (no tier rule)'
                                    : params.group === 'off_tier'
                                      ? '◦ On this order (not in tier)'
                                      : '✕ Unpriced (no tier rule, no Shopify match)';
                                // Group-header backgrounds — SOLID (not translucent) so the
                                // sticky header during scroll actually obscures the rows it
                                // floats over, instead of letting them bleed through (which
                                // looked like overlapping text in the dropdown).
                                const bg = params.group === 'tier'
                                  ? '#E8F5E9'
                                  : params.group === 'shopify_fallback'
                                    ? '#FFF3E0'
                                    : params.group === 'off_tier'
                                      ? '#ECEFF1'
                                      : '#FFEBEE';
                                const color = params.group === 'tier'
                                  ? 'success.dark'
                                  : params.group === 'shopify_fallback'
                                    ? 'warning.dark'
                                    : params.group === 'off_tier'
                                      ? 'text.secondary'
                                      : 'error.dark';
                                return (
                                  <li key={params.key}>
                                    <Box sx={{
                                      position: 'sticky', top: 0, zIndex: 2,
                                      bgcolor: bg, color, fontWeight: 700, fontSize: 11,
                                      px: 1.5, py: 0.5, textTransform: 'uppercase', letterSpacing: 0.5,
                                      borderBottom: '1px solid rgba(0,0,0,0.08)',
                                    }}>{label}</Box>
                                    <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>{params.children}</ul>
                                  </li>
                                );
                              }}
                              renderInput={(params) => <TextField {...params}
                                placeholder={
                                  !selectedCustomer ? 'Pick a customer first'
                                    : !brandId ? 'Pick a brand to load SKUs'
                                    : `${tierSkus.length} tier ${tierSkus.length === 1 ? 'SKU' : 'SKUs'} — type a SKU or name to search all products`
                                } />}
                              renderOption={(props, option) => {
                                const isTier = option.price_source === 'tier';
                                const isFallback = option.price_source === 'shopify_fallback';
                                const isUnpriced = option.price_source === 'unpriced';
                                const availQty = Number(option.available_quantity || 0);
                                const inbQty = Number(option.inbound_quantity || 0);
                                const showQty = option.available_quantity != null;
                                // 3-row layout per option to avoid left/right column squeeze.
                                // Row 1: SKU (left) + price (right). Row 2: product name.
                                // Row 3: optional fallback/unpriced note + stock note.
                                return (
                                  <Box component="li" {...props} key={option.sku}
                                    sx={{
                                      display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.75,
                                      ...(isTier ? { bgcolor: 'rgba(76, 175, 80, 0.04)' } : {}),
                                      ...(isFallback ? { bgcolor: 'rgba(255, 167, 38, 0.05)' } : {}),
                                      ...(isUnpriced ? { bgcolor: 'rgba(244, 67, 54, 0.05)' } : {}),
                                    }}>
                                    {(option.thumbnail_url || option.image_url) ? (
                                      <Box component="img" src={option.thumbnail_url || option.image_url}
                                        sx={{ width: 40, height: 40, borderRadius: 0.5, objectFit: 'cover', border: '1px solid #ddd', flexShrink: 0 }}
                                        onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                                    ) : (
                                      <Box sx={{ width: 40, height: 40, borderRadius: 0.5, bgcolor: 'grey.200', flexShrink: 0 }} />
                                    )}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      {/* Row 1: SKU + price */}
                                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1, minWidth: 0 }}>
                                          {option.sku}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace', flexShrink: 0, whiteSpace: 'nowrap' }}>
                                          {option.currency} {Number(option.unit_price || 0).toFixed(2)}
                                        </Typography>
                                      </Box>
                                      {/* Row 2: product name (own line, truncates with ellipsis) */}
                                      <Typography variant="caption" color="textSecondary" noWrap sx={{ display: 'block', lineHeight: 1.3 }}>
                                        {option.product_name || '(no name)'}
                                      </Typography>
                                      {/* Row 3: fallback/unpriced note OR stock at warehouse */}
                                      {(isFallback || isUnpriced || showQty) && (
                                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.25, minWidth: 0 }}>
                                          {(isFallback || isUnpriced) && (
                                            <Typography variant="caption" sx={{
                                              flex: 1, minWidth: 0,
                                              fontStyle: 'italic', fontSize: 10, lineHeight: 1.2,
                                              color: isFallback ? 'warning.dark' : 'error.dark',
                                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                            }}>
                                              {isFallback
                                                ? `no tier rule — using Shopify ${option.fallback_region?.toUpperCase()} price`
                                                : 'no tier rule, no Shopify match — set price manually'}
                                            </Typography>
                                          )}
                                          {showQty && (
                                            <Typography variant="caption" sx={{
                                              ml: 'auto', flexShrink: 0, whiteSpace: 'nowrap',
                                              fontSize: 10, lineHeight: 1.2, fontFamily: 'monospace',
                                              color: availQty > 0 ? 'success.dark' : 'error.dark',
                                            }}>
                                              {availQty > 0 ? `${availQty.toLocaleString()} avail` : 'out of stock'}
                                              {inbQty > 0 && ` (+${inbQty.toLocaleString()} inbound)`}
                                            </Typography>
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              }}
                              disabled={quantitiesLocked || !selectedCustomer}
                              noOptionsText={selectedCustomer
                                ? 'No products in this tier for this brand — add tier rules via Retail → Price Tiers'
                                : 'Select a customer to load tier SKUs'}
                            />
                            {l.product_sku && (() => {
                              const item = skuOptions.find(s => s.sku === l.product_sku);
                              return item?.product_name ? (
                                <Typography variant="caption" color="textSecondary" noWrap sx={{ display: 'block' }}>
                                  {item.product_name}
                                </Typography>
                              ) : null;
                            })()}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={l.quantity_ordered}
                          disabled={quantitiesLocked}
                          onChange={(e) => {
                            const v = Math.max(0, Number(e.target.value) || 0);
                            // Auto-sync dispatched when it had been auto-tracking the ordered qty
                            const patch = { quantity_ordered: v };
                            if (Number(l.quantity_dispatched) === Number(l.quantity_ordered)) {
                              patch.quantity_dispatched = v;
                            }
                            updateLine(idx, patch);
                          }}
                          inputProps={{ min: 0, step: 1, style: { textAlign: 'right' } }}
                          sx={{ width: 90 }} />
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={l.quantity_dispatched}
                          disabled={quantitiesLocked}
                          onChange={(e) => {
                            const v = Math.max(0, Math.min(Number(e.target.value) || 0, Number(l.quantity_ordered) || 0));
                            updateLine(idx, { quantity_dispatched: v });
                          }}
                          inputProps={{ min: 0, max: l.quantity_ordered || 0, step: 1, style: { textAlign: 'right' } }}
                          sx={{ width: 90 }} />
                        {isShort && (
                          <Typography variant="caption" color="warning.dark" display="block" sx={{ fontWeight: 600 }}>
                            short by {qOrd - qDisp}
                          </Typography>
                        )}
                        {/* Inventory available at the selected warehouse for THIS sku.
                            Italics, small font, dimmer color when unknown. Renders only
                            when a SKU is picked AND backend returned available_quantity
                            (i.e. a warehouse was selected when the tier loaded). The
                            over-allocation warning is informational only — the order
                            still saves so the operator can override when system stock
                            differs from physical (e.g. recent count not yet entered). */}
                        {l.product_sku && (() => {
                          const item = tierSkus.find(s => s.sku === l.product_sku);
                          if (!item || item.available_quantity == null) return null;
                          const avail = Number(item.available_quantity);
                          const inbound = Number(item.inbound_quantity || 0);
                          // Over-reservation = qDisp > (current + inbound by ship date).
                          // Inbound only counts when operator set expected_ship_date;
                          // backend returns null otherwise.
                          const hasInbound = item.inbound_quantity != null && inbound > 0;
                          const effectiveSupply = avail + inbound;
                          const overReserved = qDisp > effectiveSupply;
                          return (
                            <Typography variant="caption" display="block"
                              sx={{
                                fontStyle: 'italic', fontSize: 10, lineHeight: 1.2,
                                color: overReserved ? 'error.main' : 'text.secondary',
                                fontWeight: overReserved ? 600 : 400,
                              }}
                              title={overReserved
                                ? 'You can still save — system stock may be outdated. Reservation will be partial.'
                                : ''}>
                              Available at {warehouseCode || 'WH'}: {avail.toLocaleString()}
                              {hasInbound && ` (+${inbound.toLocaleString()} inbound by ${expectedShipDate})`}
                              {overReserved ? ` — over-allocating by ${qDisp - effectiveSupply} (override allowed)` : ''}
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        <TextField size="small" type="number" value={l.unit_price}
                          disabled={quantitiesLocked || !!l.is_gift}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              // Cleared → genuinely BLANK → tier price fills in on save.
                              updateLine(idx, { unit_price: '', override: false, is_gift: false });
                            } else {
                              const v = Math.max(0, Number(raw) || 0);
                              // Any typed price is an override that must stick. A typed 0 is
                              // NOT read as free — "free" is the Free (FOC) tick and nothing
                              // else. Inferring is_gift from v === 0 here also disabled this
                              // very field mid-keystroke (disabled={!!l.is_gift}), so the
                              // first "0" of "0.50" locked the line at zero and flagged it a
                              // give-away. Any sub-£1 price was untypeable.
                              updateLine(idx, { unit_price: v, override: true });
                            }
                          }}
                          inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }}
                          sx={{ width: 110 }} />
                        <FormControlLabel
                          sx={{ m: 0, mt: 0.25, '& .MuiFormControlLabel-label': { fontSize: 10 } }}
                          control={
                            <Checkbox size="small" sx={{ p: 0.25 }} checked={!!l.is_gift}
                              disabled={quantitiesLocked || !l.product_sku}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  // Free → deliberate 0 override that survives save + dispatch.
                                  updateLine(idx, { is_gift: true, override: true, unit_price: 0 });
                                } else {
                                  // Un-free → restore the SKU's tier/fallback price (mirrors onPickSku).
                                  // An unpriced SKU (no tier rule, no fallback) goes back to BLANK,
                                  // never to 0 — blank is rejected by the backend, 0 would ship free.
                                  const t = tierSkus.find(s => s.sku === l.product_sku);
                                  if (t && t.price_source === 'tier') {
                                    updateLine(idx, { is_gift: false, override: false, unit_price: Number(t.unit_price) || '' });
                                  } else if (t && Number(t.unit_price) > 0) {
                                    updateLine(idx, { is_gift: false, override: true, unit_price: Number(t.unit_price) });
                                  } else {
                                    updateLine(idx, { is_gift: false, override: false, unit_price: '' });
                                  }
                                }
                              }} />
                          }
                          label="Free (FOC)"
                        />
                        {/* A typed 0 that isn't ticked Free is ambiguous to a human too —
                            point them at the control that expresses the intent, rather than
                            letting them hit save and collect a "no price found" error. */}
                        {!l.is_gift && l.override && l.unit_price === 0 && (
                          <Typography variant="caption" color="error.main" display="block" sx={{ fontSize: 10, lineHeight: 1.2, fontWeight: 600 }}>
                            £0 — tick Free (FOC) to give this away
                          </Typography>
                        )}
                        {l.override && !l.is_gift && l.unit_price !== 0 && <Typography variant="caption" color="warning.main" display="block">override</Typography>}
                        {l.tier_mismatch && (
                          <Typography variant="caption" color="error.main" display="block" sx={{ fontSize: 10, lineHeight: 1.2, fontWeight: 600 }}>
                            Price tier mismatch — tier {Number(l.tier_mismatch.tier_price).toFixed(2)}
                          </Typography>
                        )}
                        {/* Fallback-pricing hint: when picked SKU's source is
                            shopify_fallback or unpriced, show a small label +
                            link to the Price Tiers admin so the operator can
                            add a proper tier rule for next time. */}
                        {l.product_sku && (() => {
                          const item = tierSkus.find(s => s.sku === l.product_sku);
                          if (!item || item.price_source === 'tier' || !item.price_source) return null;
                          const tierLabel = activeCustomer?.tier_name || selectedCustomer?.tier_name;
                          const tierLabelQuoted = tierLabel ? `"${tierLabel}"` : 'this tier';
                          const canAddToTier = !!activeCustomer?.price_tier_id;
                          return (
                            <>
                              <Typography variant="caption" sx={{ display: 'block', fontSize: 10, lineHeight: 1.2, color: item.price_source === 'unpriced' ? 'error.main' : 'warning.dark', fontStyle: 'italic' }}>
                                {item.price_source === 'shopify_fallback'
                                  ? `Shopify ${item.fallback_region?.toUpperCase()} fallback`
                                  : 'no Shopify match'}
                                {' · '}
                                <RouterLink to="/inventory/price-tiers" target="_blank" rel="noopener noreferrer"
                                  style={{ color: 'inherit', textDecoration: 'underline' }}
                                  title={`Open Price Tiers admin for ${tierLabelQuoted}`}>
                                  Price Tiers admin
                                </RouterLink>
                              </Typography>
                              {/* CHANGE 3 — one-click add of this un-tiered SKU to the
                                  customer's price tier, without leaving the order. */}
                              {canAddToTier && (
                                <Button size="small" variant="text" startIcon={<AddIcon fontSize="small" />}
                                  disabled={addingTierSku === l.product_sku}
                                  onClick={() => handleAddToTier(idx)}
                                  sx={{ mt: 0.25, p: 0, minWidth: 0, fontSize: 10, textTransform: 'none', lineHeight: 1.2 }}
                                  title={`Add ${l.product_sku} to ${tierLabelQuoted}${Number(l.unit_price) > 0 ? ` at ${Number(l.unit_price).toFixed(2)}` : ''}`}>
                                  {addingTierSku === l.product_sku ? 'Adding…' : 'Add to tier'}
                                </Button>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={l.hs_code || ''}
                          disabled={quantitiesLocked}
                          onChange={(e) => updateLine(idx, { hs_code: e.target.value })}
                          sx={{ width: 110 }}
                          placeholder="33049990" />
                      </TableCell>
                      <TableCell>
                        <TextField size="small" value={l.country_of_origin || ''}
                          disabled={quantitiesLocked}
                          onChange={(e) => updateLine(idx, { country_of_origin: e.target.value })}
                          sx={{ width: 120 }}
                          placeholder="Italy" />
                      </TableCell>
                      <TableCell align="right">{total.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        {(() => {
                          // Margin per line = (price - cost) / price * 100.
                          // Render '—' when cost is unknown (no product_cogs row at warehouse).
                          // Use the order-currency cost (backend-converted from USD) so
                          // price and cost are the same currency before subtracting.
                          const item = tierSkus.find(s => s.sku === l.product_sku);
                          const cost = item?.unit_cost_order_ccy != null ? Number(item.unit_cost_order_ccy)
                                     : (item?.unit_cost != null ? Number(item.unit_cost) : null);
                          const price = Number(l.unit_price) || 0;
                          // Cost unknown → nothing to show (unchanged).
                          if (cost == null || cost < 0) return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
                          // DT-1453: cost in another currency with no exchange rate → no margin.
                          const ccyMismatch = costCurrencyMismatch(item, selectedCustomer?.currency);
                          if (ccyMismatch) {
                            return (
                              <span style={{ color: '#999', fontStyle: 'italic' }}
                                title={ccyMismatch.costCcy === 'unknown currency'
                                  ? `The cost's currency is not recorded, so it cannot be compared with a ${ccyMismatch.priceCcy} price`
                                  : `Cost is in ${ccyMismatch.costCcy} and could not be converted to ${ccyMismatch.priceCcy} (no exchange rate)`}>
                                —
                              </span>
                            );
                          }
                          // Deliberate give-away → there's no margin; surface the give-away
                          // COST (cost × dispatched qty) in red so the operator sees what the
                          // freebie costs us rather than a bare '—'.
                          // Keyed on the explicit Free tick, NOT on price <= 0: an unpriced
                          // line reads as 0 too, and calling that a give-away is the same
                          // "0 means free" conflation this screen is being cured of.
                          if (l.is_gift === true) {
                            const giveaway = cost * qDisp;
                            return (
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}
                                title={`Free (FOC) — give-away cost ${cost.toFixed(2)}/unit × ${qDisp} = ${giveaway.toFixed(2)}`}>
                                Free · −{giveaway.toFixed(2)}
                              </Typography>
                            );
                          }
                          // No usable price yet (blank/unpriced, or a typed 0 that the backend
                          // will tier-price on save) → margin isn't computable. Don't guess.
                          if (price <= 0) return <span style={{ color: '#999', fontStyle: 'italic' }}>—</span>;
                          const pct = ((price - cost) / price) * 100;
                          const neg = pct < 0;
                          return (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: neg ? 'error.dark' : (pct < 15 ? 'warning.dark' : 'success.dark') }}
                              title={`Cost ${cost.toFixed(2)} · Margin ${(price - cost).toFixed(2)}/unit`}>
                              {pct.toFixed(0)}%
                            </Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">
                        {lines.length > 1 && (
                          <IconButton size="small" onClick={() => removeLine(idx)} disabled={quantitiesLocked}><DeleteIcon fontSize="small" /></IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 1.5 }} />
          {/* Totals block — pinned to viewport top while the operator scrolls
              through line items above (sticky position with top offset = AppBar
              height). Adds a subtle elevation so the running totals always feel
              "always-on". */}
          <Grid container spacing={1.5} justifyContent="flex-end">
            <Grid item xs={12} md={4} sx={{
              '& > .MuiBox-root': { fontSize: 13, py: 0.25 },
              position: { md: 'sticky' },
              top: { md: 88 },
              alignSelf: 'flex-start',
              bgcolor: 'background.paper',
              borderRadius: 1,
              p: 1.25,
              boxShadow: { md: '0 -8px 16px -16px rgba(0,0,0,0.18), 0 4px 16px -8px rgba(0,0,0,0.10)' },
            }}>
              <Box display="flex" justifyContent="space-between">
                <span>Qty Ordered</span><strong>{totals.qtyOrdered}</strong>
              </Box>
              <Box display="flex" justifyContent="space-between"
                sx={{ color: totals.qtyDispatched < totals.qtyOrdered ? 'warning.dark' : 'inherit', fontWeight: totals.qtyDispatched < totals.qtyOrdered ? 600 : 400 }}>
                <span>Qty Dispatched{totals.qtyDispatched < totals.qtyOrdered ? ` — short ${totals.qtyOrdered - totals.qtyDispatched}` : ''}</span>
                <strong>{totals.qtyDispatched}</strong>
              </Box>
              <Box display="flex" justifyContent="space-between">
                <span>Gross{taxMode === 'inclusive' ? ` (incl. ${taxTerm.toLowerCase()})` : ''}</span>
                <strong>{totals.gross.toFixed(2)}</strong>
              </Box>
              <Box display="flex" justifyContent="space-between"><span>Discount</span><strong>−{totals.discount.toFixed(2)}</strong></Box>
              <Divider sx={{ my: 0.75 }} />
              {taxMode === 'inclusive' ? (
                <>
                  <Box display="flex" justifyContent="space-between">
                    <span>Subtotal (excl. {taxTerm.toLowerCase()})</span><strong>{totals.net.toFixed(2)}</strong>
                  </Box>
                  <Box display="flex" justifyContent="space-between" sx={{ color: 'text.secondary' }}>
                    <span>Of which {taxTerm.toLowerCase()} ({totals.taxPct}%)</span>
                    <strong>{totals.taxAmount.toFixed(2)}</strong>
                  </Box>
                </>
              ) : (
                <>
                  <Box display="flex" justifyContent="space-between">
                    <span>Subtotal (net of discount)</span><strong>{totals.net.toFixed(2)}</strong>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <span>{taxTerm} {taxMode === 'exempt' ? '— exempt' : `(${totals.taxPct}%${taxLabel && taxLabel !== `${totals.taxPct}%` ? ` — ${taxLabel}` : ''})`}</span>
                    <strong>{totals.taxAmount.toFixed(2)}</strong>
                  </Box>
                </>
              )}
              <Box display="flex" justifyContent="space-between"><span>Freight</span><strong>{totals.freight.toFixed(2)}</strong></Box>
              <Box display="flex" justifyContent="space-between"><span>Surcharge</span><strong>{totals.surcharge.toFixed(2)}</strong></Box>
              <Divider sx={{ my: 0.75 }} />
              <Box display="flex" justifyContent="space-between" sx={{ fontSize: 16, py: 0.5 }}>
                <strong>Grand Total</strong>
                {/* DT-1453: no currency on the tier = a plain number, never labelled USD */}
                <strong>{selectedCustomer?.currency ? `${selectedCustomer.currency} ` : ''}{totals.grandTotal.toFixed(2)}</strong>
              </Box>
              {/* Margin block — informational. Shown only when at least one
                  line has a known cost. Lines with unknown cost are excluded
                  from the cost total, so partial-coverage warnings show. */}
              {totals.marginPct != null && (
                <Box sx={{ mt: 0.5, fontSize: 11, color: 'text.secondary', fontStyle: 'italic' }}>
                  <Box display="flex" justifyContent="space-between">
                    <span>Cost basis ({totals.costKnownLines}/{lines.length} lines)</span>
                    <span>{totals.totalCost.toFixed(2)}</span>
                  </Box>
                  <Box display="flex" justifyContent="space-between" sx={{
                    color: totals.marginPct < 0 ? 'error.dark' : (totals.marginPct < 15 ? 'warning.dark' : 'success.dark'),
                    fontWeight: 600, fontStyle: 'normal'
                  }}>
                    <span>Est. Margin</span>
                    <span>{totals.marginAmount.toFixed(2)} ({totals.marginPct.toFixed(1)}%)</span>
                  </Box>
                  {lines.length - totals.costKnownLines - totals.costCcyMismatchLines > 0 && (
                    <Box sx={{ fontSize: 10, color: 'text.disabled' }}>
                      {lines.length - totals.costKnownLines - totals.costCcyMismatchLines} line(s) excluded — no product_cogs at {warehouseCode || 'WH'}
                    </Box>
                  )}
                </Box>
              )}
              {totals.costCcyMismatchLines > 0 && (
                <Box sx={{ mt: 0.5, fontSize: 10, color: 'text.disabled', fontStyle: 'italic' }}>
                  {totals.costCcyMismatchLines} line(s) left out of the margin: cost could not be converted ({totals.costCcyMismatchPairs.join(', ')}: no exchange rate, or the cost's currency is not recorded)
                </Box>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ position: 'sticky', bottom: 0, bgcolor: 'background.default', py: 1, borderTop: '1px solid', borderColor: 'divider', zIndex: 1 }}>
        {/* Success alert appears INLINE with the action bar so the user sees
            the confirmation right where they clicked Submit — no scroll-up. */}
        {/* Info-only notice — brand change, line reset, etc. NO jump buttons. */}
        {notice && (
          <Alert severity="info" sx={{ mb: 1 }} onClose={() => setNotice(null)}>
            <Typography variant="body2">{notice}</Typography>
          </Alert>
        )}
        {/* Order created/updated — has jump buttons to wholesale queues. */}
        {success && (
          <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess(null)}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
              <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>{success}</Typography>
              <Button size="small" variant="outlined" onClick={() => navigate('/retail/orders')}>
                Open Orders
              </Button>
              <Button size="small" onClick={() => navigate('/wholesale/ready-to-invoice')}>
                Ready-to-Invoice
              </Button>
            </Box>
          </Alert>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          {/* Destructive actions are only meaningful in edit mode and only
              for not-yet-shipped orders. Backend enforces both: void requires
              non-voided + non-invoiced; delete only allowed on pending/draft. */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {isEditMode && !viewOnlyReason && (
              <>
                <Button size="small" variant="outlined" color="warning"
                  startIcon={<VoidIcon fontSize="small" />}
                  onClick={() => { setError(null); setVoidOpen(true); }}
                  disabled={saving || voiding}>
                  Void Order
                </Button>
                <Button size="small" variant="outlined" color="error"
                  startIcon={<TrashIcon fontSize="small" />}
                  onClick={() => { setError(null); setDeleteConfirmOpen(true); }}
                  disabled={saving || deleting}>
                  Delete
                </Button>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Dispatch / Ship / Invoice are driven by the Fulfilment date fields
                above Delivery & Billing (Rishabh 2026-06-30) — removed from here. */}
            <Button size="small" onClick={() => navigate(listBackTo)} disabled={saving || savingDraft}>
              {viewOnlyReason ? 'Back' : 'Cancel'}
            </Button>
            {!viewOnlyReason && !isEditMode && (
              <Button size="small" variant="outlined" onClick={handleSaveDraft} disabled={savingDraft || saving || !brandId}>
                {savingDraft ? 'Saving…' : (draftId ? 'Update Draft' : 'Save as Draft')}
              </Button>
            )}
            {/* Show the single Save on editable orders AND on dispatched/shipped (view-only)
                orders: the lines are locked there, but dispatch/delivered/invoice must still
                be saveable (this button is now the only save path). Hidden only for voided. */}
            {(!viewOnlyReason || (isEditMode && !isVoidedOrder)) && (
              <Button size="small" variant="contained" startIcon={<SaveIcon fontSize="small" />} onClick={handleSaveAll} disabled={saving || savingFulfil || (!viewOnlyReason && !canSubmit)}>
                {saving
                  ? (isEditMode ? 'Updating…' : (draftMode ? 'Finalizing…' : 'Creating…'))
                  : (isEditMode ? 'Save Changes' : (draftMode ? 'Finalize Order' : 'Create + Reserve Inventory'))}
              </Button>
            )}
          </Box>
        </Box>

        {renderRevertDialog()}

        {/* Mark Invoiced — set on the order page itself, same endpoint as the Ready-to-Invoice tab. */}
        <Dialog open={invoiceOpen} onClose={() => setInvoiceOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Mark order invoiced</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
              Records the QuickBooks invoice on this order and moves it to Invoiced — no need to open the Ready-to-Invoice tab.
            </Typography>
            <TextField fullWidth autoFocus label="Invoice number *" sx={{ mb: 2 }}
              value={invoiceNumberInput} onChange={(e) => setInvoiceNumberInput(e.target.value)}
              placeholder="QB invoice #" disabled={invoicing} />
            <TextField fullWidth type="date" label="Invoice date" InputLabelProps={{ shrink: true }}
              value={invoiceDateInput} onChange={(e) => setInvoiceDateInput(e.target.value)}
              helperText="Leave blank to use today" disabled={invoicing} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setInvoiceOpen(false)} disabled={invoicing}>Cancel</Button>
            <Button variant="contained" onClick={() => handleMarkInvoiced()} disabled={invoicing || !invoiceNumberInput.trim()}>
              {invoicing ? 'Marking…' : 'Mark Invoiced'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Void dialog — reason required for audit trail. */}
        <Dialog open={voidOpen} onClose={() => setVoidOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Void this order?</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Alert severity="warning" sx={{ mb: 2 }}>
              Voiding releases the inventory reservations and marks the order is_voided=1.
              Reason is required for the audit trail. This cannot be undone via the UI.
            </Alert>
            <TextField fullWidth multiline rows={2} label="Reason *" required autoFocus
              value={voidReason} onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Customer cancelled, duplicate order, wrong tier applied"/>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setVoidOpen(false)} disabled={voiding}>Cancel</Button>
            <Button variant="contained" color="warning" startIcon={<VoidIcon />}
              disabled={voiding || !voidReason.trim()}
              onClick={async () => {
                try {
                  setVoiding(true);
                  await voidWholesaleOrder(parseInt(editBrandId), editOrderId, voidReason.trim());
                  setSuccess(`Voided ${editOrderId.replace(/^CIN7-/, '')} — reason logged.`);
                  setVoidOpen(false);
                  setTimeout(() => navigate(listBackTo), 800);
                } catch (e) {
                  setError(e.response?.data?.error || e.message || 'Failed to void');
                } finally {
                  setVoiding(false);
                }
              }}>
              {voiding ? 'Voiding…' : 'Confirm Void'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete dialog — hard delete (only for orders that never shipped). */}
        <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Delete this order?</DialogTitle>
          <DialogContent>
            {/* DT-1922: this dialog stays open when its action fails; the page Alert sits behind the modal. */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <Alert severity="error" sx={{ mb: 2 }}>
              This permanently removes the order row + releases reservations. Cannot be undone.
              Use Void instead if you want to keep the audit record. Delete is only allowed for
              pending / not-yet-shipped orders.
            </Alert>
            <Typography variant="body2">
              Order <code>{(editOrderId || '').replace(/^CIN7-/, '')}</code>
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
            <Button variant="contained" color="error" startIcon={<TrashIcon />}
              disabled={deleting}
              onClick={async () => {
                try {
                  setDeleting(true);
                  await deleteRetailOrder(parseInt(editBrandId), editOrderId);
                  setSuccess(`Deleted ${editOrderId.replace(/^CIN7-/, '')}`);
                  setDeleteConfirmOpen(false);
                  setTimeout(() => navigate(listBackTo), 800);
                } catch (e) {
                  setError(e.response?.data?.error || e.message || 'Failed to delete');
                } finally {
                  setDeleting(false);
                }
              }}>
              {deleting ? 'Deleting…' : 'Confirm Delete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}
